import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from './DatePicker';
import RezervasyonDatePicker from './RezervasyonDatePicker';
import { apiRequest } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useKasa } from '../context/KasaContext';

function tcDogrula(tc) {
  if (!tc || tc.trim().length === 0) return null;
  tc = tc.trim();
  if (tc.length !== 11) return 'T.C. kimlik numarası 11 haneli olmalıdır.';
  if (!/^\d+$/.test(tc)) return 'T.C. kimlik numarası yalnızca rakamlardan oluşmalıdır.';
  if (tc[0] === '0') return 'T.C. kimlik numarasının ilk hanesi 0 olamaz.';
  if (tc === tc[0].repeat(11)) return 'Geçersiz T.C. kimlik numarası.';
  const d = tc.split('').map(Number);
  const d10 = ((7 * (d[0] + d[2] + d[4] + d[6] + d[8]) - (d[1] + d[3] + d[5] + d[7])) % 10 + 10) % 10;
  if (d[9] !== d10) return 'Geçersiz T.C. kimlik numarası (kontrol hanesi hatalı).';
  const d11 = d.slice(0, 10).reduce((a, b) => a + b, 0) % 10;
  if (d[10] !== d11) return 'Geçersiz T.C. kimlik numarası (kontrol hanesi hatalı).';
  return null; // geçerli
}

function pasaportDogrula(pasaport) {
  if (!pasaport || pasaport.trim().length === 0) return null;
  pasaport = pasaport.trim();
  if (pasaport.length < 6 || pasaport.length > 15) return 'Pasaport numarası 6 ile 15 karakter arasında olmalıdır.';
  if (!/^[A-Za-z0-9]+$/.test(pasaport)) return 'Pasaport numarası yalnızca harf ve rakamlardan oluşmalıdır.';
  return null; // geçerli
}

function yasHesapla(dogumTarihi) {
  if (!dogumTarihi) return null;
  let dogum;
  if (typeof dogumTarihi === 'string' && dogumTarihi.includes('.')) {
    const [g, a, y] = dogumTarihi.split('.').map(Number);
    dogum = new Date(y, a - 1, g);
  } else {
    dogum = new Date(dogumTarihi);
  }
  if (!dogum || isNaN(dogum.getTime())) return null;
  const bugun = new Date();
  let yas = bugun.getFullYear() - dogum.getFullYear();
  const ay = bugun.getMonth() - dogum.getMonth();
  if (ay < 0 || (ay === 0 && bugun.getDate() < dogum.getDate())) yas--;
  return yas;
}

export default function YeniRezervasyonModal({ onClose, varsayilanOda, varsayilanTarih, onKaydedildi }) {
  const { personel } = useAuth();
  const { odemeTipleri, bankalar, dovizKurlari } = useKasa();
  const [aktifTab, setAktifTab] = useState(1);
  const [misafirler, setMisafirler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [karaListeUyari, setKaraListeUyari] = useState(null); // { adSoyad, tcNo, neden }
  const [cakismaUyari, setCakismaUyari] = useState(null); // { odaNo, giris, cikis }

  // Dropdown verileri
  const [odalar, setOdalar] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [konaklamaTurleri, setKonaklamaTurleri] = useState([]);
  const [paraBirimleri, setParaBirimleri] = useState([]);
  const [misafirTipleri, setMisafirTipleri] = useState([]);

  // Kişi sayısı (fiyatlandırma için — misafir listesinden bağımsız seçilebilir)
  const [kisiSayisi, setKisiSayisi] = useState(1);
  const [cocukSayisi, setCocukSayisi] = useState(0);
  const [cocukYaslari, setCocukYaslari] = useState([]); // her çocuğun yaşı
  const [cocukUcretsiz, setCocukUcretsiz] = useState([]); // her çocuk için manuel ücretsiz override
  const [cocukKurallari, setCocukKurallari] = useState([]); // CocukFiyatKurali listesi

  // Misafir formu
  const [yeniMisafir, setYeniMisafir] = useState({
    tc: '', pasaportNo: '', pasaportGecerlilik: '',
    adSoyad: '', dogum: '', dogumYeri: '',
    ulke: 'TÜRKİYE', uyruk: '', telefon: '',
    email: '', cinsiyet: 'Erkek', meslek: '', adres: '', grupTip: 'Yetişkin', misafirTipiId: '',
    anaAdi: '', babaAdi: '', aracPlaka: ''
  });
  const [tcArama, setTcArama] = useState(false);
  const [tcBulundu, setTcBulundu] = useState(false);

  // Kayıtlı misafirler
  const [kayitliMisafirler, setKayitliMisafirler] = useState([]);
  const [misafirArama, setMisafirArama] = useState('');

  // Tarih
  const bugun = new Date();
  const formatTarih = (d) => [
    String(d.getDate()).padStart(2, '0'),
    String(d.getMonth() + 1).padStart(2, '0'),
    d.getFullYear()
  ].join('.');

  // varsayilanTarih ISO (YYYY-MM-DD) → DD.MM.YYYY
  const isoToFmt = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  const baslangicTarih = varsayilanTarih ? isoToFmt(varsayilanTarih) : formatTarih(bugun);
  const baslangicCikis = (() => {
    const d = varsayilanTarih ? new Date(varsayilanTarih) : new Date(bugun);
    d.setDate(d.getDate() + 1);
    return formatTarih(d);
  })();

  const [girisTarihi, setGirisTarihi] = useState(baslangicTarih);
  const [cikisTarihi, setCikisTarihi] = useState(baslangicCikis);
  const [cikisBelir, setCikisBelir] = useState(false);
  const [belirsizKilit, setBelirsizKilit] = useState(false);
  const [belirsizKilitGun, setBelirsizKilitGun] = useState(30);
  const [girisSaati, setGirisSaati] = useState('14:00');
  const [cikisSaati, setCikisSaati] = useState('12:00');
  const [secilenOdaId, setSecilenOdaId] = useState(varsayilanOda || '');
  const [secilenFirmaId, setSecilenFirmaId] = useState('');
  const [pansiyonTipiId, setPansiyonTipiId] = useState('');
  const [not, setNot] = useState('');
  const [otomatikUzatma, setOtomatikUzatma] = useState(false);
  const [odaCheckIn, setOdaCheckIn] = useState(false);

  // Fiyat
  const [fiyatTipi, setFiyatTipi] = useState('Toplam fiyat');
  const [odaTutari, setOdaTutari] = useState(0);
  const [gunlukFiyatlar, setGunlukFiyatlar] = useState([]); // Günlük-Özel fiyat için
  const [otomatikFiyat, setOtomatikFiyat] = useState(null); // hesaplanan fiyat
  const [fiyatYukleniyor, setFiyatYukleniyor] = useState(false);
  const [dolulukCarpan, setDolulukCarpan] = useState(null); // doluluk bazlı fiyat artışı
  const [saatCarpan, setSaatCarpan] = useState(null); // saat bazlı fiyat değişimi
  const [paraBirimi, setParaBirimi] = useState('TL');
  const [kaporaAlindi, setKaporaAlindi] = useState(false);
  const [kaporaTutari, setKaporaTutari] = useState('');
  const [kaporaOdemeTipi, setKaporaOdemeTipi] = useState('');
  const [kaporaParaBirimi, setKaporaParaBirimi] = useState('TL');
  const [kaporaBankaAdi, setKaporaBankaAdi] = useState('');

  // Firma fiyat seçenekleri
  const [firmaKurallari, setFirmaKurallari] = useState([]); // uygun kurallar + hesaplanmış fiyatlar
  const [secilenFirmaKuraliId, setSecilenFirmaKuraliId] = useState(null);

  // fiyatTipi ref — fiyatHesapla deps'ine eklemeden mevcut değeri okumak için
  const fiyatTipiRef = useRef(fiyatTipi);
  useEffect(() => { fiyatTipiRef.current = fiyatTipi; }, [fiyatTipi]);
  const firmaIdRef = useRef(secilenFirmaId);
  useEffect(() => { firmaIdRef.current = secilenFirmaId; }, [secilenFirmaId]);
  const firmaKurallariRef = useRef([]);
  useEffect(() => { firmaKurallariRef.current = firmaKurallari; }, [firmaKurallari]);

  // Oda seçilince dolu tarihlerini çek
  useEffect(() => {
    if (!secilenOdaId) { setDoluTarihler([]); return; }
    apiRequest(`oda/${secilenOdaId}/dolu-tarihler`)
      .then(data => setDoluTarihler(data || []))
      .catch(() => setDoluTarihler([]));
  }, [secilenOdaId]);

  // Seçili odanın dolu tarihleri (takvimde kırmızı gösterilir)
  const [doluTarihler, setDoluTarihler] = useState([]);

  // ── odemeTipleri yüklenince kapora varsayılanını ayarla ─────────
  useEffect(() => {
    if (odemeTipleri.length > 0 && !kaporaOdemeTipi) {
      const nakit = odemeTipleri.find(o => o.ad.toLowerCase().includes('nakit'));
      setKaporaOdemeTipi((nakit || odemeTipleri[0]).ad);
    }
  }, [odemeTipleri]);

  // ── paraBirimleri yüklenince TL'yi varsayılan yap, yoksa ilk öğeyi al ────────
  useEffect(() => {
    if (paraBirimleri.length > 0 && !paraBirimleri.some(pb => pb.tcmbKod === paraBirimi)) {
      const tl = paraBirimleri.find(pb => pb.tcmbKod === 'TL' || pb.tcmbKod === 'TRY');
      setParaBirimi((tl || paraBirimleri[0]).tcmbKod);
    }
    // eslint-disable-next-line
  }, [paraBirimleri]);

  // ── Kapora para birimi her zaman rezervasyon para birimiyle senkron olsun ─────
  useEffect(() => {
    setKaporaParaBirimi(paraBirimi);
  }, [paraBirimi]);

  // ── Çocuk sayısı değişince yaş ve ücretsiz dizilerini senkronize et ───────────
  useEffect(() => {
    setCocukYaslari(prev => {
      const yeni = Array(cocukSayisi).fill(0);
      for (let i = 0; i < Math.min(prev.length, cocukSayisi); i++) yeni[i] = prev[i];
      return yeni;
    });
    setCocukUcretsiz(prev => {
      const yeni = Array(cocukSayisi).fill(false);
      for (let i = 0; i < Math.min(prev.length, cocukSayisi); i++) yeni[i] = prev[i];
      return yeni;
    });
  }, [cocukSayisi]);

  // ── Dropdown verileri yükle ──────────────────────────
  useEffect(() => {
    const fetchDropdowns = async () => {
      setYukleniyor(true);
      try {
        const [odaData, firmaData, konData, pbData, cocukData, mtData] = await Promise.all([
          apiRequest('oda/musait').catch(() => null),
          apiRequest('firmalar').catch(() => null),
          apiRequest('konaklamaturleri').catch(() => null),
          apiRequest('ParaBirimi').catch(() => null),
          apiRequest('cocukfiyatkurallari').catch(() => null),
          apiRequest('misafirtipleri').catch(() => null),
        ]);
        if (odaData) setOdalar(odaData.filter(o => o.odaTipiId && o.odaGrubuId).sort((a, b) => String(a.odaNo || '').localeCompare(String(b.odaNo || ''), undefined, { numeric: true, sensitivity: 'base' })));
        if (firmaData) setFirmalar(firmaData.filter(f => f.aktifMi));
        if (konData) setKonaklamaTurleri(konData);
        if (pbData) setParaBirimleri(pbData);
        if (cocukData) setCocukKurallari(cocukData.filter(k => k.aktifMi));
        if (mtData) setMisafirTipleri(mtData);
      } catch (err) {
        console.error('Dropdown verileri yüklenemedi:', err);
      } finally {
        setYukleniyor(false);
      }
    };
    fetchDropdowns();
  }, []);

  // ── Misafir tipi bazlı fiyat hesabı (per-guest) ─────────────────
  const yetiskinMisafirler = misafirler.filter(m => m.grupTip !== 'Çocuk');
  const ilkYetiskinTip = yetiskinMisafirler.length > 0 && yetiskinMisafirler[0].misafirTipiId
    ? misafirTipleri.find(t => t.misafirTipiId === parseInt(yetiskinMisafirler[0].misafirTipiId))
    : null;
  const misafirTipiGecelikFiyat = ilkYetiskinTip?.gecelikFiyat || 0;
  const misafirTipiGecelikAktif = misafirTipiGecelikFiyat > 0;
  const misafirTipiKisiBasiGecelik = yetiskinMisafirler.reduce((sum, m) => {
    const tip = m.misafirTipiId ? misafirTipleri.find(t => t.misafirTipiId === parseInt(m.misafirTipiId)) : null;
    return sum + (tip?.kisiBazliFiyat || 0);
  }, 0);
  const misafirTipiKisiBasiAktif = misafirTipiKisiBasiGecelik > 0;

  // ── ODA + TARİH + MİSAFİR değişince fiyat hesapla ──────────────
  const fiyatHesapla = useCallback(async () => {
    if (!secilenOdaId || !girisTarihi) return;
    const _kur = (paraBirimi && paraBirimi !== 'TL' && paraBirimi !== 'TRY' && dovizKurlari[paraBirimi] > 0)
      ? dovizKurlari[paraBirimi] : 1;
    const tlToKur = (tl) => _kur > 1 ? Math.round(tl / _kur * 100) / 100 : tl;
    if (!cikisBelir && !cikisTarihi) return;

    const secilenOda = odalar.find(o => o.odaId === parseInt(secilenOdaId));
    if (!secilenOda?.odaTipiId) return;

    const gunAdlari = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const secilenPansiyon = konaklamaTurleri.find(k => k.konaklamaTuruId === parseInt(pansiyonTipiId));
    const pansiyonEkFiyat = secilenPansiyon?.ekFiyat || 0;

    setFiyatYukleniyor(true);
    try {
      // Firma seçili VE uygun firma kuralı varsa normal oda fiyatı hesaplamasını atla
      if (secilenFirmaId) {
        const _firma = firmalar.find(f => f.firmaId === parseInt(secilenFirmaId));
        if (_firma?.fiyatKurallari?.length > 0) {
          const _odaTipiId = secilenOda.odaTipiId;
          const _uygun = _firma.fiyatKurallari.some(k => !k.odaTipiId || k.odaTipiId === _odaTipiId);
          if (_uygun) return;
        }
      }
      
      const parsedGiris = parseTarih(girisTarihi);
      const parsedCikis = !cikisBelir && cikisTarihi ? parseTarih(cikisTarihi) : null;
      const fmtISO = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
      const girisISO = fmtISO(parsedGiris);
      const cikisISO = fmtISO(parsedCikis);

      const [fiyatData, carpanData, saatCarpanData] = await Promise.all([
        apiRequest(`odafiyat/${secilenOda.odaTipiId}`),
        girisISO ? apiRequest(`dolulukfiyat/carpan?odaTipiId=${secilenOda.odaTipiId}&giris=${girisISO}${cikisISO ? `&cikis=${cikisISO}` : ''}`).catch(() => null) : Promise.resolve(null),
        girisSaati ? apiRequest(`saatfiyat/carpan?odaTipiId=${secilenOda.odaTipiId}&saat=${encodeURIComponent(girisSaati)}`).catch(() => null) : Promise.resolve(null),
      ]);
      if (!fiyatData) return;
      // Saklanan fiyat para birimi → TL dönüşümü
      const fiyatPB = fiyatData.gunlukFiyatlar?.[0]?.paraBirimi || fiyatData.kisiBasiFiyatlar?.[0]?.paraBirimi || 'TL';
      const fiyatKur = (fiyatPB && fiyatPB !== 'TL' && fiyatPB !== 'TRY' && dovizKurlari[fiyatPB] > 0)
        ? dovizKurlari[fiyatPB] : 1;
      const fTL = (f) => (f || 0) * fiyatKur; // saklanan birim → TL
      const dolulukC = carpanData?.carpan ?? 1;
      const saatC = saatCarpanData?.carpan ?? 1;

      // ── Gecelik / Saatlik Ayrımı ──
      // 1. Çıkış tarihi belirsiz ise (cikisBelir) varsayılan olarak geceliktir.
      // 2. Çıkış tarihi belirli ise, giriş ve çıkış arasındaki GÜN farkı 1 veya daha fazla ise geceliktir.
      // EĞER gecelik ise, saatCarpan (saatlik kısa kiralama çarpanı) ASLA uygulanmaz.
      const gece = !cikisBelir ? geceSayisiHesapla(girisTarihi, cikisTarihi) : 1;
      const isGecelikRez = cikisBelir || gece >= 1;

      const carpan = isGecelikRez ? dolulukC : dolulukC * saatC;

      setDolulukCarpan((carpanData?.carpan !== 1 && carpanData?.carpan != null) ? carpanData : null);
      setSaatCarpan((!isGecelikRez && saatCarpanData?.carpan !== 1 && saatCarpanData?.carpan != null) ? saatCarpanData : null);

      // ── Kişi Bazlı Hesap (tanımlıysa her zaman hesapla) ─────────
      let kisiBasiHesap = null;
      if (fiyatData.kisiBasiFiyatlar?.length > 0) {
        const sirali = [...fiyatData.kisiBasiFiyatlar].sort((a, b) => a.kisiSayisi - b.kisiSayisi);
        const uygun = sirali.filter(k => k.kisiSayisi <= kisiSayisi).pop() || sirali[0];
        const kb_gecelikOda = fTL(uygun?.fiyat) * carpan;
        const kb_gecelikToplam = kb_gecelikOda + pansiyonEkFiyat;
        if (cikisBelir) {
          kisiBasiHesap = { gecelik: kb_gecelikToplam, gecelikOda: kb_gecelikOda, pansiyonEk: pansiyonEkFiyat, kisiSayisi, belirsiz: true };
        } else {
          kisiBasiHesap = { gunluk: kb_gecelikOda, pansiyonEk: pansiyonEkFiyat, toplamGunluk: kb_gecelikToplam, toplam: kb_gecelikToplam * gece, odaToplam: kb_gecelikOda * gece, gece, kisiSayisi };
        }
      }
      const kisiBasiMevcut = kisiBasiHesap !== null;

      // ── Belirsiz Modu ─────────────────────────────────────────────
      if (cikisBelir) {
        const girisD = parseTarih(girisTarihi);
        const ozel = girisD && fiyatData.ozelFiyatlar?.find(f => {
          const bas = new Date(f.baslangicTarihi); const bit = new Date(f.bitisTarihi);
          return girisD >= bas && girisD < bit;
        });
        let gecelikOda = 0;
        if (ozel) { gecelikOda = fTL(ozel.fiyat); }
        else if (girisD) {
          const gunAdi = gunAdlari[girisD.getDay()];
          gecelikOda = fTL(fiyatData.gunlukFiyatlar?.find(f => f.gun === gunAdi)?.fiyat);
        }
        const gecelikToplam = gecelikOda * carpan + pansiyonEkFiyat;
        const gecelikOdaCarpanli = gecelikOda * carpan;
        setOtomatikFiyat({ gecelik: gecelikToplam, gecelikOda: gecelikOdaCarpanli, pansiyonEk: pansiyonEkFiyat, kisiBasiAktif: false, belirsiz: true, kisiBasiMevcut, kisiBasiHesap });

        // Belirsiz modda kişi bazlı seçili mi? → o gecelik fiyatı kullan
        const currentFT = fiyatTipiRef.current;
        if (currentFT === 'Kişi bazlı gecelik' && kisiBasiMevcut) {
          setFiyatTipi('Kişi bazlı gecelik');
          setOdaTutari(tlToKur(kisiBasiHesap.gecelik));
        } else {
          setFiyatTipi('Gecelik ücret');
          setOdaTutari(tlToKur(gecelikToplam));
        }
        return;
      }

      // ── Günlük Fiyat Hesapla ──────────────────────────────────────
      const girisD = parseTarih(girisTarihi);
      let toplamFiyat = 0;
      let geceDetaylari = [];
      for (let i = 0; i < gece; i++) {
        const gun = new Date(girisD); gun.setDate(girisD.getDate() + i);
        const ozel = fiyatData.ozelFiyatlar?.find(f => {
          const bas = new Date(f.baslangicTarihi); const bit = new Date(f.bitisTarihi);
          return gun >= bas && gun < bit;
        });
        let gunFiyat = 0;
        if (ozel) { gunFiyat = fTL(ozel.fiyat); }
        else { const gunAdi = gunAdlari[gun.getDay()]; gunFiyat = fTL(fiyatData.gunlukFiyatlar?.find(f => f.gun === gunAdi)?.fiyat); }
        toplamFiyat += gunFiyat;
        geceDetaylari.push(gunFiyat);
      }
      const genelToplam = toplamFiyat * carpan + pansiyonEkFiyat * gece;
      const ortalamaGunluk = gece > 0 ? toplamFiyat * carpan / gece : 0;

      setOtomatikFiyat({
        gunluk: ortalamaGunluk, pansiyonEk: pansiyonEkFiyat, toplamGunluk: ortalamaGunluk + pansiyonEkFiyat,
        toplam: genelToplam, odaToplam: toplamFiyat * carpan, gece, kisiBasiAktif: false,
        karma: geceDetaylari.some((f, _, arr) => f !== arr[0]),
        kisiBasiMevcut, kisiBasiHesap,
      });

      // Mevcut fiyatTipi'ni koru; sadece "otomatik" modlardan birindeyse güncelle
      const currentFT = fiyatTipiRef.current;
      const isManualMode = currentFT === 'Günlük-Özel fiyat' || currentFT === 'Fiyatı sabitle'
        || currentFT === 'Misafir Tipi Gecelik' || currentFT === 'Misafir Tipi Kişi Bazlı';
      if (!isManualMode) {
        if (currentFT === 'Kişi bazlı fiyat' && kisiBasiMevcut) {
          setOdaTutari(tlToKur(kisiBasiHesap.toplam));
        } else {
          setFiyatTipi('Toplam fiyat');
          setOdaTutari(tlToKur(genelToplam));
        }
      }
    } catch {
      // fiyat bulunamazsa sessiz geç
    } finally {
      setFiyatYukleniyor(false);
    }
  }, [secilenOdaId, girisTarihi, cikisTarihi, cikisBelir, girisSaati, odalar, pansiyonTipiId, konaklamaTurleri, kisiSayisi, paraBirimi, dovizKurlari]);

  useEffect(() => { fiyatHesapla(); }, [fiyatHesapla]);

  useEffect(() => {
    if (aktifTab === 2 && kayitliMisafirler.length === 0) {
      apiRequest('misafirler').then(d => setKayitliMisafirler(d || [])).catch(() => { });
    }
  }, [aktifTab]);

  // ── TC ile misafir ara ───────────────────────────────
  const tcAra = useCallback(async (tc) => {
    if (!tc || tc.trim().length < 11) return; // TC min 11 hane
    setTcArama(true); setTcBulundu(false);
    try {
      const m = await apiRequest(`misafirler/tc/${tc.trim()}`);
      if (m && m.misafirId && m.misafirId > 0) { // gerçek kayıt kontrolü
        const formatDB = (isoStr) => {
          if (!isoStr) return '';
          const d = new Date(isoStr);
          return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        };
        setYeniMisafir({
          tc: m.tcPasaportNo || tc,
          adSoyad: m.adSoyad || '',
          dogum: formatDB(m.dogumTarihi),
          dogumYeri: m.dogumYeri || '',
          ulke: m.ulke || 'TÜRKİYE',
          telefon: m.telefon || '',
          email: m.email || '',
          cinsiyet: m.cinsiyet || 'Erkek',
          meslek: m.meslek || '',
          adres: m.adres || '',
          anaAdi: m.anaAdi || '',
          babaAdi: m.babaAdi || '',
          aracPlaka: m.aracPlaka || '',
        });
        setTcBulundu(true);
      } else {
        setTcBulundu(false);
      }
    } catch { setTcBulundu(false); }
    setTcArama(false);
  }, []);

  // ── Yardımcı fonksiyonlar ────────────────────────────
  const parseTarih = (str) => {
    if (!str) return null;
    const [g, a, y] = str.split('.').map(Number);
    return new Date(y, a - 1, g);
  };
  const isoTarih = (str) => {
    if (!str) return null;
    const [g, a, y] = str.split('.').map(Number);
    return new Date(Date.UTC(y, a - 1, g)).toISOString();
  };
  const geceSayisiHesapla = (giris, cikis) => {
    const g = parseTarih(giris), c = parseTarih(cikis);
    if (!g || !c) return 1;
    const diff = Math.round((c - g) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 1;
  };
  const geceSayisi = geceSayisiHesapla(girisTarihi, cikisTarihi);

  // Bir çocuğun gecelik ücretini hesapla (yetişkin gecelik fiyatı baz alınır)
  const cocukGecelikFiyat = (yas, yetiskinGecelik) => {
    const kural = cocukKurallari.find(k => yas >= k.minYas && yas <= k.maxYas);
    if (!kural || kural.fiyatTipi === 'UCRETSIZ') return 0;
    if (kural.fiyatTipi === 'YUZDE') return Math.round(yetiskinGecelik * kural.deger / 100);
    if (kural.fiyatTipi === 'SABIT') return kural.deger;
    return 0;
  };

  // Firma kisi-basi kural için kişi sayısına göre gecelik toplam hesapla
  const firmaKisiBasiGecelik = (kural, n) => {
    if (!kural || kural.fiyatTipi !== 'kisi-basi') return 0;
    if (kural.kisiBasiTip === 'kademeli') {
      const detaylar = [...(kural.kisiDetayFiyatlar || [])].sort((a, b) => a.kisiSirasi - b.kisiSirasi);
      if (!detaylar.length) return 0;
      let total = 0;
      for (let i = 1; i <= n; i++) {
        const entry = detaylar.find(d => d.kisiSirasi === i) || detaylar[detaylar.length - 1];
        total += Number(entry.fiyat);
      }
      return total;
    }
    return Number(kural.fiyat) * n; // sabit
  };

  // ── Firma seçilince uygun kuralları hesapla ──────────────────────────────
  useEffect(() => {
    if (!secilenFirmaId) { setFirmaKurallari([]); setSecilenFirmaKuraliId(null); return; }
    const firma = firmalar.find(f => f.firmaId === parseInt(secilenFirmaId));
    if (!firma?.fiyatKurallari?.length) { setFirmaKurallari([]); setSecilenFirmaKuraliId(null); return; }

    const secilenOda = odalar.find(o => o.odaId === parseInt(secilenOdaId));
    const odaTipiId = secilenOda?.odaTipiId || null;
    const gece = Math.max(geceSayisi, 1);

    // Her kural için: oda tipine özel varsa genel kuralı ezendir (aynı fiyatTipi+kisiBasiTip kombinasyonu)
    const uygun = firma.fiyatKurallari.filter(k => {
      if (k.odaTipiId && k.odaTipiId !== odaTipiId) return false; // başka oda tipine ait
      // Genel kural ise aynı tip+alt-tip için özel kural varsa atla
      if (!k.odaTipiId) {
        const ozelVar = firma.fiyatKurallari.some(o =>
          o.firmaFiyatKuraliId !== k.firmaFiyatKuraliId &&
          o.odaTipiId === odaTipiId &&
          o.fiyatTipi === k.fiyatTipi &&
          (k.fiyatTipi !== 'kisi-basi' || o.kisiBasiTip === k.kisiBasiTip)
        );
        if (ozelVar) return false;
      }
      return true;
    });

    const computed = uygun.map(k => {
      // Toplam çarpan: Kural net fiyatı × KDV × KonaklamaVergisi
      const kdvO = firma.kdvOrani || 0;
      const kvO = firma.konaklamaVergisi || 0;
      const totalCarpan = (1 + kdvO / 100) * (1 + kvO / 100);

      let bazGecelik = k.fiyatTipi === 'gecelik'
        ? Number(k.fiyat)
        : firmaKisiBasiGecelik(k, kisiSayisi);

      const gecelikToplam = bazGecelik * totalCarpan;
      const toplam = gecelikToplam * gece;

      const pb = k.paraBirimi || 'TRY';
      const yabanci = pb !== 'TL' && pb !== 'TRY';
      const kurD = yabanci && dovizKurlari[pb] > 0 ? dovizKurlari[pb] : 1;
      const tryGecelik = yabanci ? gecelikToplam * kurD : null;
      const tryToplam = yabanci ? toplam * kurD : null;

      const fmtN = n => n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
      let label, detayStr;
      if (k.fiyatTipi === 'gecelik') {
        label = 'Gecelik';
        detayStr = cikisBelir
          ? `${fmtN(gecelikToplam)} ${pb} / gece`
          : `${fmtN(gecelikToplam)} × ${gece} gece = ${fmtN(toplam)} ${pb}`;
      } else if (k.kisiBasiTip === 'sabit') {
        label = 'Kişi Başı (Sabit)';
        detayStr = cikisBelir
          ? `${fmtN(Number(k.fiyat) * totalCarpan)} × ${kisiSayisi} kişi = ${fmtN(gecelikToplam)} ${pb}/gece`
          : `${fmtN(Number(k.fiyat) * totalCarpan)} × ${kisiSayisi} kişi × ${gece} gece = ${fmtN(toplam)} ${pb}`;
      } else {
        const detaylar = [...(k.kisiDetayFiyatlar || [])].sort((a, b) => a.kisiSirasi - b.kisiSirasi);
        const kisiStr = Array.from({ length: kisiSayisi }, (_, i) => {
          const e = detaylar.find(d => d.kisiSirasi === i + 1) || detaylar[detaylar.length - 1];
          return fmtN(Number(e?.fiyat || 0) * totalCarpan);
        }).join('+');
        label = 'Kişi Başı (Kademeli)';
        detayStr = cikisBelir
          ? `(${kisiStr}) = ${fmtN(gecelikToplam)} ${pb}/gece`
          : `(${kisiStr}) × ${gece} gece = ${fmtN(toplam)} ${pb}`;
      }
      return { kural: k, label, detayStr, gecelikToplam, toplam, paraBirimi: pb, tryGecelik, tryToplam };
    });

    setFirmaKurallari(computed);
    setSecilenFirmaKuraliId(prev => {
      const halaGecerli = computed.find(c => c.kural.firmaFiyatKuraliId === prev);
      return halaGecerli ? prev : (computed[0]?.kural.firmaFiyatKuraliId ?? null);
    });
  }, [secilenFirmaId, secilenOdaId, geceSayisi, cikisBelir, kisiSayisi, odalar, firmalar, dovizKurlari]);

  // ── Seçili firma kuralı değişince fiyatı uygula ──────────────────────────
  useEffect(() => {
    if (!secilenFirmaKuraliId || !firmaKurallari.length) return;
    const sel = firmaKurallari.find(c => c.kural.firmaFiyatKuraliId === secilenFirmaKuraliId);
    if (!sel) return;
    const { kural, gecelikToplam, toplam, tryGecelik, tryToplam } = sel;
    const gece = Math.max(geceSayisi, 1);
    const kisiBasiMevcut = firmaKurallari.some(c => c.kural.fiyatTipi === 'kisi-basi');

    // Yabancı para birimli kurallarda TRY karşılığını kullan
    const efektifGecelik = tryGecelik ?? gecelikToplam;
    const efektifToplam = tryToplam ?? toplam;
    const efektifPb = tryGecelik != null ? 'TRY' : (kural.paraBirimi || 'TRY');

    if (cikisBelir) {
      const ft = kural.fiyatTipi === 'kisi-basi' ? 'Kişi bazlı gecelik' : 'Gecelik ücret';
      setFiyatTipi(ft);
      setOdaTutari(efektifGecelik);
      setOtomatikFiyat({
        belirsiz: true, gecelik: efektifGecelik, gecelikOda: efektifGecelik, pansiyonEk: 0,
        kisiBasiAktif: kural.fiyatTipi === 'kisi-basi', kisiBasiMevcut,
        kisiBasiHesap: kural.fiyatTipi === 'kisi-basi'
          ? { gecelik: efektifGecelik, gecelikOda: efektifGecelik, pansiyonEk: 0, kisiSayisi, belirsiz: true }
          : null,
      });
    } else {
      const ft = kural.fiyatTipi === 'kisi-basi' ? 'Kişi bazlı fiyat' : 'Toplam fiyat';
      setFiyatTipi(ft);
      setOdaTutari(efektifToplam);
      setOtomatikFiyat({
        gece, gunluk: efektifGecelik, pansiyonEk: 0, toplamGunluk: efektifGecelik,
        toplam: efektifToplam, odaToplam: efektifToplam, karma: false,
        kisiBasiAktif: kural.fiyatTipi === 'kisi-basi', kisiBasiMevcut,
        kisiBasiHesap: kural.fiyatTipi === 'kisi-basi'
          ? { gunluk: efektifGecelik, pansiyonEk: 0, toplamGunluk: efektifGecelik, toplam: efektifToplam, odaToplam: efektifToplam, gece, kisiSayisi }
          : null,
      });
    }
    setParaBirimi(efektifPb);
  }, [secilenFirmaKuraliId, firmaKurallari, geceSayisi, cikisBelir, kisiSayisi]);

  // Kapasite aşımı: toplam kişi sayısı odanın kapasitesini geçerse
  // sadece kapasite dışında kalan çocuklar ücretlendirilir
  const secilenOdaObj = odalar.find(o => o.odaId === parseInt(secilenOdaId));
  const odaKapasitesi = secilenOdaObj?.kisiKapasitesi || 999;
  const kapasiteAsimi = Math.max(0, kisiSayisi + cocukSayisi - odaKapasitesi);
  const ucretliCocukSayisi = Math.min(cocukSayisi, kapasiteAsimi);
  // i. çocuk ücretli mi? Kapasite aşımından ücretli olsa bile manuel ücretsiz işaretlendiyse ücretsiz
  const cocukUcretliMi = (i) => {
    if (cocukUcretsiz[i]) return false;
    return i >= cocukSayisi - ucretliCocukSayisi;
  };

  // Toplam çocuk ücreti — kapasite içindeki ve manuel ücretsiz işaretlenen çocuklar dahil edilmez
  const cocukToplamFiyat = (yetiskinGecelik) => {
    return cocukYaslari.reduce((acc, yas, i) => {
      if (!cocukUcretliMi(i)) return acc;
      return acc + cocukGecelikFiyat(yas, yetiskinGecelik) * (cikisBelir ? 1 : geceSayisi);
    }, 0);
  };

  const handleGirisTarihiDegis = (val) => {
    setGirisTarihi(val);
    const g = parseTarih(val);
    if (g) { const c = new Date(g); c.setDate(c.getDate() + geceSayisi); setCikisTarihi(formatTarih(c)); }
  };
  const geceArttir = () => {
    const g = parseTarih(girisTarihi);
    if (g) { const c = new Date(g); c.setDate(c.getDate() + geceSayisi + 1); setCikisTarihi(formatTarih(c)); }
  };
  const geceAzalt = () => {
    if (geceSayisi <= 1) return;
    const g = parseTarih(girisTarihi);
    if (g) { const c = new Date(g); c.setDate(c.getDate() + geceSayisi - 1); setCikisTarihi(formatTarih(c)); }
  };

  const BOŞ_MİSAFİR = { tc: '', pasaportNo: '', pasaportGecerlilik: '', adSoyad: '', dogum: '', dogumYeri: '', ulke: 'TÜRKİYE', uyruk: '', telefon: '', email: '', cinsiyet: 'Erkek', meslek: '', adres: '', firma: '', grupTip: 'Yetişkin', misafirTipiId: '' };

  const misafirEkle = () => {
    if (!yeniMisafir.adSoyad) return;

    // Kimlik doğrulama
    const turkiye = (yeniMisafir.ulke || '').toUpperCase() === 'TÜRKİYE' || (yeniMisafir.ulke || '').toUpperCase() === 'TURKIYE';
    if (turkiye) {
      if (!yeniMisafir.tc) { setHata('Türkiye uyruklu misafir için T.C. kimlik numarası zorunludur.'); return; }
      const tcHata = tcDogrula(yeniMisafir.tc);
      if (tcHata) { setHata(tcHata); return; }
    } else {
      if (!yeniMisafir.pasaportNo) { setHata('Yabancı uyruklu misafir için pasaport numarası zorunludur.'); return; }
      const pasHata = pasaportDogrula(yeniMisafir.pasaportNo);
      if (pasHata) { setHata(pasHata); return; }
    }

    // Aynı TC/Pasaport listeye eklenmemiş olmalı
    const tcTekrar = turkiye && yeniMisafir.tc && misafirler.some(m => m.tc === yeniMisafir.tc);
    const pasTekrar = !turkiye && yeniMisafir.pasaportNo && misafirler.some(m => m.pasaportNo === yeniMisafir.pasaportNo);
    if (tcTekrar || pasTekrar) { setHata('Bu misafir zaten listeye eklenmiş.'); return; }

    const maxMisafir = kisiSayisi + cocukSayisi;
    if (misafirler.length >= maxMisafir) {
      setHata(`Seçilen kişi sayısı ${maxMisafir} (${kisiSayisi} yetişkin + ${cocukSayisi} çocuk). Daha fazla misafir eklenemez.`);
      return;
    }
    const yeniListe = [...misafirler, yeniMisafir];
    setMisafirler(yeniListe);
    // Auto-suggest grupTip for next guest
    const ekYetiskin = yeniListe.filter(m => m.grupTip === 'Yetişkin').length;
    const sonrakiGrupTip = ekYetiskin < kisiSayisi ? 'Yetişkin' : 'Çocuk';
    setYeniMisafir({ ...BOŞ_MİSAFİR, grupTip: sonrakiGrupTip });
    setTcBulundu(false);
  };

  function tarihDoluMu(tarihStr, araliklar) {
    if (!tarihStr || !araliklar?.length) return false;
    const [g, a, y] = tarihStr.split('.').map(Number);
    const d = new Date(y, a - 1, g);
    d.setHours(0, 0, 0, 0);
    return araliklar.some(aralik => {
      const giris = new Date(aralik.girisTarihi); giris.setHours(0, 0, 0, 0);
      if (!aralik.cikisTarihi) return d >= giris;
      const cikis = new Date(aralik.cikisTarihi); cikis.setHours(0, 0, 0, 0);
      return d >= giris && d < cikis;
    });
  }

  const handleKaydet = async () => {
    if (!secilenOdaId) { setHata('Lütfen oda seçiniz.'); setAktifTab(1); return; }
    if (!girisTarihi) { setHata('Giriş tarihi seçiniz.'); setAktifTab(1); return; }
    if (tarihDoluMu(girisTarihi, doluTarihler)) { setHata('Seçilen giriş tarihi bu oda için dolu / bloke edilmiş.'); setAktifTab(1); return; }
    if (!cikisBelir && !cikisTarihi) { setHata('Çıkış tarihi seçiniz veya "Belirsiz" işaretleyiniz.'); setAktifTab(1); return; }
    if (misafirler.length === 0) { setHata('En az bir misafir ekleyiniz.'); setAktifTab(2); return; }
    const toplamMisafirSayisi = kisiSayisi + cocukSayisi;
    if (misafirler.length < toplamMisafirSayisi) {
      setHata(`${kisiSayisi} yetişkin + ${cocukSayisi} çocuk = ${toplamMisafirSayisi} misafir bekleniyor, ${misafirler.length} kişi eklendi.`);
      setAktifTab(2); return;
    }
    const maxMisafir = kisiSayisi + cocukSayisi;
    if (misafirler.length > maxMisafir) {
      setHata(`Misafir sayısı (${misafirler.length}) seçilen kişi sayısını (${maxMisafir}) aşıyor.`);
      setAktifTab(2);
      return;
    }
    setKaydediliyor(true); setHata(null); setCakismaUyari(null);
    await kaydetGonder(false);
    setKaydediliyor(false);
  };

  const kaydetGonder = async (karaListeOnaylandi) => {
    try {
      const rezervasyon = await apiRequest('rezervasyonlar', 'POST', {
        rezNo: Date.now().toString().slice(-8),
        girisTarihi: isoTarih(girisTarihi), girisSaati,
        cikisTarihi: cikisBelir ? null : isoTarih(cikisTarihi), cikisSaati,
        faturalamaTipiId: null,
        pansiyonTipiId: pansiyonTipiId ? parseInt(pansiyonTipiId) : null,
        misafirTipiId: (() => {
          const ilk = misafirler.find(m => m.grupTip !== 'Çocuk');
          return ilk?.misafirTipiId ? parseInt(ilk.misafirTipiId) : null;
        })(),
        durum: 'Check IN Bekliyor',
        odaCheckInYapildi: odaCheckIn,
        // 'Kişi bazlı gecelik' backend'de 'Gecelik ücret' olarak saklanır (C-OUT mantığı aynı)
        fiyatTipi: fiyatTipi === 'Kişi bazlı gecelik' ? 'Gecelik ücret' : fiyatTipi,
        odaTutari: parseFloat(odaTutari) || 0, paraBirimi,
        kurDegeri: (paraBirimi && paraBirimi !== 'TL' && paraBirimi !== 'TRY' && dovizKurlari[paraBirimi] > 0) ? dovizKurlari[paraBirimi] : 1,
        kaporaAlindi,
        kisiSayisi: kisiSayisi || 1,
        cocukSayisi: cocukSayisi || 0,
        cocukYaslari: cocukYaslari,
        not, otomatikUzatmaAktif: otomatikUzatma,
        belirsizCikisKilitGun: cikisBelir && belirsizKilit ? parseInt(belirsizKilitGun) || null : null,
        odaId: parseInt(secilenOdaId),
        firmaId: secilenFirmaId ? parseInt(secilenFirmaId) : null,
        personelId: personel?.personelId || 1,
        karaListeOnaylandi,
        misafirler: misafirler.map(m => ({
          adSoyad: m.adSoyad,
          tcPasaportNo: m.ulke === 'TÜRKİYE' ? m.tc : null,
          pasaportNo: m.ulke !== 'TÜRKİYE' ? m.pasaportNo : null,
          pasaportGecerlilikTarihi: isoTarih(m.pasaportGecerlilik),
          uyruk: m.uyruk,
          dogumTarihi: isoTarih(m.dogum),
          dogumYeri: m.dogumYeri,
          ulke: m.ulke,
          telefon: m.telefon,
          email: m.email,
          cinsiyet: m.cinsiyet,
          meslek: m.meslek,
          adres: m.adres,
          anaAdi: m.anaAdi,
          babaAdi: m.babaAdi,
          aracPlaka: m.aracPlaka,
          firma: m.firma || null,
          grupTip: m.grupTip || 'Yetişkin',
          misafirTipiId: m.misafirTipiId ? parseInt(m.misafirTipiId) : null,
        })),
      });
      if (rezervasyon) {
        // Kapora alındıysa otomatik ödeme kaydı oluştur
        if (kaporaAlindi && parseFloat(kaporaTutari) > 0) {
          const now = new Date();
          const kurDegeri = dovizKurlari[kaporaParaBirimi] || 1;
          const bankaGerekli = kaporaOdemeTipi && !kaporaOdemeTipi.toLowerCase().includes('nakit') && bankalar.length > 0;
          const pad = n => String(n).padStart(2, '0');
          const localISO = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
          await apiRequest('odemeler', 'POST', {
            aciklama: 'Rezervasyon kapora ödemesi',
            tutar: parseFloat(kaporaTutari),
            paraBirimi: kaporaParaBirimi,
            kurDegeri,
            odemeTipi: kaporaOdemeTipi,
            bankaAdi: bankaGerekli ? kaporaBankaAdi : '',
            odemeTarihi: localISO,
            odemeSaati: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
            rezervasyonId: rezervasyon.rezervasyonId,
            personelId: personel?.personelId || 1,
          });
        }
        onKaydedildi?.();
        onClose();
      }
    } catch (err) {
      const msg = err.message || 'Kayıt sırasında hata oluştu.';
      if (msg.startsWith('KARALISTE:')) {
        // format: KARALISTE:{adSoyad}:{tcNo}:{neden}
        const parts = msg.split(':');
        setKaraListeUyari({ adSoyad: parts[1], tcNo: parts[2], neden: parts.slice(3).join(':') });
      } else if (msg.startsWith('CAKISMA:')) {
        const parts = msg.split(':');
        setCakismaUyari({ adSoyad: parts[1], giris: parts[2], cikis: parts[3] });
        setAktifTab(1);
      } else {
        setHata(msg);
      }
    }
    setKaydediliyor(false);
  };

  const inputClass = "w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all hover:border-indigo-300";
  const labelClass = "text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block";

  return createPortal(
    <>
      {karaListeUyari && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ backdropFilter: 'blur(12px)', background: 'rgba(15,23,42,0.7)' }}>
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-white/20" style={{ animation: 'fadeIn 0.25s ease-out' }}>
            <div className="bg-gradient-to-r from-rose-600 to-red-600 px-6 py-5 flex items-center gap-3">
              <span className="text-2xl">🚫</span>
              <div>
                <h2 className="text-white text-sm font-black uppercase tracking-widest">Kara Liste Uyarısı</h2>
                <p className="text-rose-200 text-[10px] font-semibold mt-0.5">Bu misafir kara listede kayıtlıdır</p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 space-y-2">
                <div className="flex gap-2 text-sm">
                  <span className="text-rose-600 font-black min-w-[70px] text-[10px] uppercase tracking-widest">Misafir:</span>
                  <span className="font-black text-slate-800 text-sm">{karaListeUyari.adSoyad}</span>
                </div>
                <div className="flex gap-2 text-sm">
                  <span className="text-rose-600 font-black min-w-[70px] text-[10px] uppercase tracking-widest">TC / Pas.:</span>
                  <span className="font-mono text-slate-700">{karaListeUyari.tcNo}</span>
                </div>
                {karaListeUyari.neden && (
                  <div className="flex gap-2 text-sm">
                    <span className="text-rose-600 font-black min-w-[70px] text-[10px] uppercase tracking-widest">Neden:</span>
                    <span className="text-slate-700 text-sm">{karaListeUyari.neden}</span>
                  </div>
                )}
              </div>
              <p className="text-slate-600 text-sm font-semibold">Yine de rezervasyon oluşturmak istiyor musunuz?</p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => setKaraListeUyari(null)}
                className="flex-1 border border-slate-200 text-slate-600 hover:bg-slate-50 font-black py-2.5 rounded-2xl text-[10px] uppercase tracking-widest transition-all">
                Vazgeç
              </button>
              <button onClick={async () => {
                setKaraListeUyari(null);
                setKaydediliyor(true);
                await kaydetGonder(true);
                setKaydediliyor(false);
              }}
                className="flex-1 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-black py-2.5 rounded-2xl text-[10px] uppercase tracking-widest transition-all shadow-lg">
                Evet, Yine de Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
      {cakismaUyari && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ backdropFilter: 'blur(12px)', background: 'rgba(15,23,42,0.7)' }}>
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-white/20" style={{ animation: 'fadeIn 0.25s ease-out' }}>
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-5 flex items-center gap-3">
              <span className="text-2xl">🏨</span>
              <h2 className="text-white text-sm font-black uppercase tracking-widest">Oda Müsait Değil</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-slate-700 text-sm leading-relaxed font-semibold">
                Bu misafirin seçilen tarihlerle çakışan aktif bir rezervasyonu bulunmaktadır.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-orange-600 font-black text-[10px] uppercase tracking-widest min-w-[80px]">Misafir:</span>
                  <span className="font-black text-slate-800 text-sm">{cakismaUyari.adSoyad}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-orange-600 font-black text-[10px] uppercase tracking-widest min-w-[80px]">Rezervasyon:</span>
                  <span className="font-bold text-slate-800 text-sm">{cakismaUyari.giris} – {cakismaUyari.cikis}</span>
                </div>
              </div>
              <p className="text-slate-400 text-xs font-semibold">Lütfen çakışmayan bir tarih aralığı seçiniz.</p>
            </div>
            <div className="px-6 pb-6">
              <button onClick={() => setCakismaUyari(null)}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-black py-2.5 rounded-2xl text-[10px] uppercase tracking-widest transition-all shadow-lg">
                Anladım, Değiştireceğim
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4"
        style={{ backdropFilter: 'blur(12px)', background: 'rgba(15,23,42,0.6)' }}>
        <div className="bg-white/95 backdrop-blur-xl sm:rounded-[2.5rem] shadow-2xl w-full sm:max-w-5xl max-h-[96vh] sm:max-h-[92vh] overflow-hidden flex flex-col rounded-t-[2.5rem] border border-white/20"
          style={{ animation: 'fadeIn 0.25s ease-out' }}>

          {/* HEADER */}
          <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-3 sm:py-4 flex-shrink-0 flex flex-col sm:flex-row sm:items-center gap-2">
            {/* Actions: mobile → top row; desktop → right side */}
            <div className="flex items-center gap-2 flex-shrink-0 order-1 sm:order-2 self-end sm:self-auto">
              {hata && <span className="text-red-200 text-xs hidden sm:inline truncate max-w-[200px]">{hata}</span>}
              <button onClick={handleKaydet} disabled={kaydediliyor}
                className="bg-white/15 hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 sm:px-5 py-2 rounded-2xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all shadow-lg whitespace-nowrap border border-white/20">
                {kaydediliyor ? '⏳' : <><span className="hidden sm:inline">💾 </span>Kaydet</>}
              </button>
              <button onClick={onClose}
                className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center text-white/70 hover:text-white transition-all flex-shrink-0 text-sm font-black">✕</button>
            </div>
            {/* Tabs: mobile → full-width bottom row; desktop → left side */}
            <div className="flex items-center gap-1 sm:gap-1.5 w-full max-w-full sm:w-auto order-2 sm:order-1 sm:mr-auto overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-0.5 sm:pb-0">
              {[1, 2, 3].map((n, i) => (
                <button key={n} onClick={() => setAktifTab(n)}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-4 py-2 sm:py-2.5 rounded-2xl text-[9px] sm:text-xs font-black uppercase tracking-tight sm:tracking-widest transition-all duration-200 whitespace-nowrap min-w-max sm:min-w-0
                  ${aktifTab === n ? 'bg-white text-indigo-700 shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                  <span className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-[9px] sm:text-xs font-black flex-shrink-0
                  ${aktifTab === n ? 'bg-indigo-600 text-white' : 'bg-white/20 text-white'}`}>{n}</span>
                  <span>{['Rezervasyon', 'Misafirler', 'Fiyat'][i]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile hata banner */}
          {hata && (
            <div className="sm:hidden bg-rose-50 border-b border-rose-200 px-4 py-2.5 text-rose-700 text-xs font-black flex items-center gap-2 flex-shrink-0">
              <span>⚠️</span><span>{hata}</span>
            </div>
          )}

          {/* CONTENT */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-slate-50 via-white to-indigo-50/20">

            {/* TAB 1 — Rezervasyon */}
            {aktifTab === 1 && (
              <div style={{ animation: 'slideIn 0.2s ease-out' }}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-5">

                    {/* Geçmiş tarih uyarısı */}
                    {girisTarihi && parseTarih(girisTarihi) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-700 font-black flex items-center gap-2 uppercase tracking-widest">
                        ⚠️ Geçmiş bir tarih seçili — retroaktif rezervasyon oluşturuyorsunuz.
                      </div>
                    )}

                    {/* Tarih & Saat */}
                    <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-violet-500 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">📅</span>
                        Tarih & Saat
                      </h3>
                      {tarihDoluMu(girisTarihi, doluTarihler) && (
                        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 font-semibold flex items-center gap-2 mb-2">
                          <span>🚫</span> Seçilen giriş tarihi bu oda için <strong>dolu / bloke</strong> edilmiş. Lütfen başka bir tarih seçin.
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><label className={labelClass}>Giriş Tarihi</label><RezervasyonDatePicker value={girisTarihi} onChange={handleGirisTarihiDegis} placeholder="GG.AA.YYYY" doluAraliklar={doluTarihler} /></div>
                        <div><label className={labelClass}>Giriş Saati</label><input type="time" value={girisSaati} onChange={e => setGirisSaati(e.target.value)} className={inputClass} /></div>
                        <div>
                          <label className={labelClass}>Çıkış Tarihi</label>
                          <div className="flex items-center gap-2">
                            <div className={`flex-1 ${cikisBelir ? 'opacity-40 pointer-events-none' : ''}`}>
                              <RezervasyonDatePicker value={cikisTarihi} onChange={setCikisTarihi} placeholder="GG.AA.YYYY" minDate={girisTarihi} doluAraliklar={doluTarihler} />
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
                              <input type="checkbox" checked={cikisBelir} onChange={e => {
                                setCikisBelir(e.target.checked);
                                if (e.target.checked) { setCikisTarihi(''); setBelirsizKilit(false); }
                                else {
                                  const g = parseTarih(girisTarihi);
                                  if (g) { const c = new Date(g); c.setDate(c.getDate() + 1); setCikisTarihi(formatTarih(c)); }
                                }
                              }} className="w-4 h-4" />
                              <span className="text-xs text-slate-500">Belirsiz</span>
                            </label>
                          </div>
                          {cikisBelir && (
                            <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2">
                              <input type="checkbox" id="belirsizKilitCb" checked={belirsizKilit} onChange={e => setBelirsizKilit(e.target.checked)} className="w-4 h-4 flex-shrink-0" />
                              <label htmlFor="belirsizKilitCb" className="text-xs text-amber-700 cursor-pointer whitespace-nowrap">Odayı kilitle</label>
                              <input
                                type="number" min="1" max="365"
                                value={belirsizKilitGun}
                                onChange={e => setBelirsizKilitGun(e.target.value)}
                                disabled={!belirsizKilit}
                                className={`w-16 text-center text-xs border rounded-lg px-2 py-1 ${belirsizKilit ? 'border-amber-300 bg-white text-amber-800' : 'border-slate-200 bg-slate-50 text-slate-300'}`}
                              />
                              <span className={`text-xs ${belirsizKilit ? 'text-amber-700' : 'text-slate-400'}`}>gün</span>
                            </div>
                          )}
                        </div>
                        <div><label className={labelClass}>Çıkış Saati</label><input type="time" value={cikisSaati} onChange={e => setCikisSaati(e.target.value)} className={inputClass} disabled={cikisBelir} /></div>
                      </div>
                    </div>

                    {/* Oda & Konaklama */}
                    <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-500 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">🏨</span>
                        Oda & Konaklama
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>Firma</label>
                          <select value={secilenFirmaId} onChange={e => setSecilenFirmaId(e.target.value)} className={inputClass}>
                            <option value="">Seçiniz</option>
                            {firmalar.map(f => <option key={f.firmaId} value={f.firmaId}>{f.ad}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Oda</label>
                          <select value={secilenOdaId} onChange={e => setSecilenOdaId(e.target.value)} className={inputClass}>
                            <option value="">Oda seçiniz</option>
                            {yukleniyor
                              ? <option disabled>Yükleniyor...</option>
                              : odalar.map(o => (
                                <option key={o.odaId} value={o.odaId}>
                                  Oda {o.odaNo}{o.odaTipi?.ad ? ` - ${o.odaTipi.ad}` : ''}
                                </option>
                              ))
                            }
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Pansiyon Tipi</label>
                          <select value={pansiyonTipiId} onChange={e => setPansiyonTipiId(e.target.value)} className={inputClass}>
                            <option value="">Seçiniz</option>
                            {konaklamaTurleri.map(k => <option key={k.konaklamaTuruId} value={k.konaklamaTuruId}>{k.ad}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Yetişkin / Çocuk</label>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 flex-1">
                              <input
                                type="number" min="1" max="20" value={kisiSayisi}
                                onChange={e => setKisiSayisi(Math.max(1, parseInt(e.target.value) || 1))}
                                className={inputClass + ' text-center font-bold'}
                              />
                              <span className="text-xs text-slate-400 whitespace-nowrap">yetişkin</span>
                            </div>
                            <span className="text-slate-300">+</span>
                            <div className="flex items-center gap-1 flex-1">
                              <input
                                type="number" min="0" max="20" value={cocukSayisi}
                                onChange={e => setCocukSayisi(Math.max(0, parseInt(e.target.value) || 0))}
                                className={inputClass + ' text-center font-bold'}
                              />
                              <span className="text-xs text-slate-400 whitespace-nowrap">çocuk</span>
                            </div>
                          </div>
                          {otomatikFiyat?.kisiBasiMevcut && (
                            <p className="text-[11px] text-indigo-500 mt-1 font-black text-[10px] uppercase tracking-widest">Kişi bazlı fiyatlandırma aktif</p>
                          )}
                        </div>
                        {/* Çocuk yaş inputları */}
                        {cocukSayisi > 0 && (
                          <div className="col-span-full mt-1">
                            <label className="text-xs font-semibold text-slate-500 block mb-1.5">Çocukların Yaşları</label>
                            <div className="flex flex-wrap gap-2">
                              {Array.from({ length: cocukSayisi }, (_, i) => (
                                <div key={i} className="flex items-center gap-1">
                                  <span className="text-xs text-slate-400">{i + 1}. çocuk:</span>
                                  <input
                                    type="number" min="0" max="17" value={cocukYaslari[i] ?? 0}
                                    onChange={e => {
                                      const yeni = [...cocukYaslari];
                                      yeni[i] = Math.max(0, Math.min(17, parseInt(e.target.value) || 0));
                                      setCocukYaslari(yeni);
                                    }}
                                    className={`w-14 border-2 rounded-lg px-2 py-1.5 text-sm text-center font-bold focus:outline-none bg-white ${cocukUcretsiz[i] ? 'border-green-300 text-green-600 focus:border-green-400' : 'border-emerald-200 focus:border-emerald-400'}`}
                                  />
                                  <span className="text-xs text-slate-400">yaş</span>
                                  <label className="flex items-center gap-1 cursor-pointer ml-1">
                                    <input
                                      type="checkbox"
                                      checked={cocukUcretsiz[i] || false}
                                      onChange={e => {
                                        const yeni = [...cocukUcretsiz];
                                        yeni[i] = e.target.checked;
                                        setCocukUcretsiz(yeni);
                                      }}
                                      className="w-3.5 h-3.5"
                                    />
                                    <span className="text-xs text-green-600 font-medium">Ücretsiz</span>
                                  </label>
                                </div>
                              ))}
                            </div>
                            {cocukKurallari.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {cocukYaslari.map((yas, i) => {
                                  const kural = cocukKurallari.find(k => yas >= k.minYas && yas <= k.maxYas);
                                  const manuelUcretsiz = cocukUcretsiz[i];
                                  return (
                                    <span key={i} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${manuelUcretsiz ? 'bg-green-100 text-green-700' :
                                      !kural ? 'bg-slate-100 text-slate-500' :
                                        kural.fiyatTipi === 'UCRETSIZ' ? 'bg-green-100 text-green-700' :
                                          'bg-amber-100 text-amber-700'
                                      }`}>
                                      {i + 1}. çocuk ({yas} yaş): {
                                        manuelUcretsiz ? 'Ücretsiz (Manuel)' :
                                          !kural ? 'Kural yok' :
                                            kural.fiyatTipi === 'UCRETSIZ' ? 'Ücretsiz' :
                                              kural.fiyatTipi === 'YUZDE' ? `%${kural.deger}` :
                                                `${kural.deger} TL`
                                      }
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={odaCheckIn} onChange={e => setOdaCheckIn(e.target.checked)} className="w-5 h-5 rounded" />
                          <span className="text-sm text-slate-600 font-medium">Oda Check-IN Yapıldı</span>
                        </label>
                      </div>
                    </div>

                    {/* Notlar */}
                    <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">📝</span>
                        Notlar
                      </h3>
                      <textarea rows={3} placeholder="Rezervasyon notu giriniz..." value={not} onChange={e => setNot(e.target.value)}
                        className={`${inputClass} resize-none`} />
                      <div className="flex gap-6 mt-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={otomatikUzatma} onChange={e => setOtomatikUzatma(e.target.checked)} className="w-4 h-4" />
                          <span className="text-sm text-slate-600">Otomatik uzatma aktif</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Gece sayacı + özet */}
                  <div>
                    <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 text-center sticky top-0">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Gece Sayısı</h3>
                      <button onClick={geceArttir} disabled={cikisBelir} className={`w-12 h-12 rounded-2xl text-lg font-black transition-all mx-auto flex items-center justify-center ${cikisBelir ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-500 hover:text-indigo-700'}`}>▲</button>
                      <div className={`text-5xl font-black my-4 select-none ${cikisBelir ? 'text-slate-300' : 'text-indigo-600'}`}>{cikisBelir ? '?' : geceSayisi}</div>
                      <button onClick={geceAzalt} disabled={geceSayisi <= 1 || cikisBelir}
                        className={`w-12 h-12 rounded-2xl text-lg font-black transition-all mx-auto flex items-center justify-center
                        ${geceSayisi <= 1 || cikisBelir ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-500 hover:text-indigo-700'}`}>▼</button>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-3">{cikisBelir ? 'belirsiz' : 'gece'}</p>
                      <div className="mt-4 pt-4 border-t border-slate-100 text-left space-y-1.5">
                        <div className="flex justify-between text-xs"><span className="text-slate-400">Giriş</span><span className="font-semibold text-slate-700">{girisTarihi}</span></div>
                        <div className="flex justify-between text-xs"><span className="text-slate-400">Çıkış</span><span className={`font-semibold ${cikisBelir ? 'text-amber-500 italic' : 'text-slate-700'}`}>{cikisBelir ? 'Belirsiz' : cikisTarihi}</span></div>

                        {/* Seçili oda bilgisi */}
                        {secilenOdaId && (() => {
                          const oda = odalar.find(o => o.odaId === parseInt(secilenOdaId));
                          if (!oda) return null;
                          return (
                            <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400">Oda</span>
                                <span className="font-bold text-slate-800">No: {oda.odaNo}</span>
                              </div>
                              {(oda.odaTipiAd || oda.odaTipi?.ad) && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-400">Oda Tipi</span>
                                  <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-lg font-black text-[10px] uppercase tracking-widest">
                                    {oda.odaTipiAd || oda.odaTipi?.ad}
                                  </span>
                                </div>
                              )}
                              {(oda.odaGrubuAd || oda.odaGrubu?.grupAdi) && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-400">Kat / Grup</span>
                                  <span className="text-slate-600 font-medium">
                                    {oda.odaGrubuAd || oda.odaGrubu?.grupAdi}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400">Kapasite</span>
                                <span className="text-slate-600">{oda.kisiKapasitesi} kişi</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400">Yetişkin</span>
                                <span className="font-semibold text-slate-800">{kisiSayisi} kişi</span>
                              </div>
                              {cocukSayisi > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-400">Çocuk</span>
                                  <span className="font-semibold text-emerald-700">{cocukSayisi} çocuk</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {pansiyonTipiId && (
                          <div className="flex justify-between text-xs"><span className="text-slate-400">Pansiyon</span>
                            <span className="text-emerald-600 font-semibold">{konaklamaTurleri.find(k => k.konaklamaTuruId === parseInt(pansiyonTipiId))?.ad}</span></div>
                        )}
                        {misafirler.length > 0 && misafirler.some(m => m.misafirTipiId) && (
                          <div className="flex justify-between text-xs gap-2">
                            <span className="text-slate-400 flex-shrink-0">Misafir Tipleri</span>
                            <span className="text-purple-600 font-semibold text-right">
                              {misafirler.map(m => {
                                const tip = m.misafirTipiId ? misafirTipleri.find(t => t.misafirTipiId === parseInt(m.misafirTipiId)) : null;
                                return tip?.ad || '—';
                              }).join(', ')}
                            </span>
                          </div>
                        )}
                        {/* Otomatik hesaplanan fiyat özeti */}
                        {fiyatYukleniyor && (
                          <div className="flex justify-between text-xs mt-2 pt-2 border-t border-slate-100">
                            <span className="text-slate-400">Fiyat hesaplanıyor...</span>
                          </div>
                        )}
                        {dolulukCarpan && !fiyatYukleniyor && (() => {
                          const kurallar = dolulukCarpan.mesaj
                            ? [...new Set(dolulukCarpan.mesaj.split(' + ').map(s => s.trim()).filter(Boolean))]
                            : [];
                          const indirim = dolulukCarpan.artisYuzde < 0;
                          return (
                            <div className={`mt-2 rounded-2xl px-3 py-2 text-xs flex items-start gap-2 ${indirim ? 'bg-indigo-50 border border-indigo-200 text-indigo-700' : 'bg-orange-50 border border-orange-200 text-orange-700'}`}>
                              <span className="text-base leading-none flex-shrink-0">{indirim ? '💙' : '🔥'}</span>
                              <div className="min-w-0">
                                <p className="font-bold">{indirim ? 'Doluluk İndirimi' : 'Yoğun Dönem Fiyatı'}</p>
                                {kurallar.map((k, i) => <p key={i}>{k}</p>)}
                                <p className="opacity-70 mt-0.5">({dolulukCarpan.musaitOda}/{dolulukCarpan.toplamOda} oda müsait)</p>
                              </div>
                            </div>
                          );
                        })()}
                        {saatCarpan && !fiyatYukleniyor && (
                          <div className={`mt-2 rounded-2xl px-3 py-2 text-xs flex items-start gap-2 ${saatCarpan.artisYuzde < 0 ? 'bg-indigo-50 border border-indigo-200 text-indigo-700' : 'bg-orange-50 border border-orange-200 text-orange-700'}`}>
                            <span className="text-base leading-none">{saatCarpan.artisYuzde < 0 ? '🕐' : '⏰'}</span>
                            <div>
                              <p className="font-bold">{saatCarpan.artisYuzde < 0 ? 'Saat İndirimi' : 'Saat Bazlı Artış'}</p>
                              <p>{saatCarpan.mesaj}</p>
                            </div>
                          </div>
                        )}
                        {otomatikFiyat && !fiyatYukleniyor && (
                          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                            {otomatikFiyat.belirsiz ? (
                              <>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-400">
                                    {fiyatTipi === 'Kişi bazlı gecelik' ? `👤 ${kisiSayisi} kişi / gece` : 'Gecelik ücret'}
                                  </span>
                                  <span className="font-semibold text-amber-600">
                                    {(odaTutari || 0).toLocaleString('tr-TR')} {paraBirimi}
                                    {paraBirimi !== 'TL' && paraBirimi !== 'TRY' && dovizKurlari[paraBirimi] > 0 && (
                                      <span className="text-slate-400 font-normal ml-1">≈ {Math.round((odaTutari || 0) * dovizKurlari[paraBirimi]).toLocaleString('tr-TR')} ₺</span>
                                    )}
                                  </span>
                                </div>
                                {otomatikFiyat.pansiyonEk > 0 && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-400">Pansiyon ek</span>
                                    <span className="font-semibold text-emerald-600">+{otomatikFiyat.pansiyonEk.toLocaleString('tr-TR')} ₺ / gece</span>
                                  </div>
                                )}
                                {cocukSayisi > 0 && (() => {
                                  const ucretsizS = cocukSayisi - ucretliCocukSayisi;
                                  return (<>
                                    {ucretsizS > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-slate-400">👶 {ucretsizS} çocuk (kapasite içi)</span>
                                        <span className="font-semibold text-green-600">Ücretsiz</span>
                                      </div>
                                    )}
                                    {cocukYaslari.slice(ucretsizS).map((yas, i) => {
                                      const fiyat = cocukGecelikFiyat(yas, odaTutari);
                                      return (
                                        <div key={i} className="flex justify-between text-xs">
                                          <span className="text-slate-400">👶 {ucretsizS + i + 1}. çocuk ({yas} yaş) / gece</span>
                                          <span className="font-semibold text-emerald-600">+{fiyat.toLocaleString('tr-TR')} ₺</span>
                                        </div>
                                      );
                                    })}
                                  </>);
                                })()}
                                <div className="flex justify-between text-xs pt-1 border-t border-slate-100">
                                  <span className="text-slate-400">Toplam</span>
                                  <span className="font-bold text-amber-500 italic">Checkout'ta hesaplanacak</span>
                                </div>
                              </>
                            ) : fiyatTipi === 'Kişi bazlı fiyat' && otomatikFiyat.kisiBasiHesap ? (
                              <>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-400">👤 {otomatikFiyat.kisiBasiHesap.kisiSayisi} kişi / gece</span>
                                  <span className="font-semibold text-indigo-600">{(otomatikFiyat.kisiBasiHesap.gunluk || 0).toLocaleString('tr-TR')} ₺</span>
                                </div>
                                {otomatikFiyat.kisiBasiHesap.pansiyonEk > 0 && (
                                  <div className="flex justify-between text-xs"><span className="text-slate-400">Pansiyon ek</span>
                                    <span className="font-semibold text-emerald-600">+{otomatikFiyat.kisiBasiHesap.pansiyonEk.toLocaleString('tr-TR')} ₺</span></div>
                                )}
                                {cocukSayisi > 0 && (() => {
                                  const ucretsizS = cocukSayisi - ucretliCocukSayisi;
                                  const gece = otomatikFiyat.kisiBasiHesap.gece;
                                  const baseG = otomatikFiyat.kisiBasiHesap.gunluk || 0;
                                  return (<>
                                    {ucretsizS > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-slate-400">👶 {ucretsizS} çocuk (kapasite içi)</span>
                                        <span className="font-semibold text-green-600">Ücretsiz</span>
                                      </div>
                                    )}
                                    {cocukYaslari.slice(ucretsizS).map((yas, i) => {
                                      const fiyat = cocukGecelikFiyat(yas, baseG);
                                      return (
                                        <div key={i} className="flex justify-between text-xs">
                                          <span className="text-slate-400">👶 {ucretsizS + i + 1}. çocuk ({yas} yaş)</span>
                                          <span className="font-semibold text-emerald-600">+{(fiyat * gece).toLocaleString('tr-TR')} ₺</span>
                                        </div>
                                      );
                                    })}
                                  </>);
                                })()}
                                {cocukSayisi > 0 && cocukToplamFiyat(otomatikFiyat.kisiBasiHesap.gunluk || 0) > 0 && (
                                  <div className="flex justify-between text-xs font-semibold text-emerald-700">
                                    <span>Çocuk toplam</span>
                                    <span>+{cocukToplamFiyat(otomatikFiyat.kisiBasiHesap.gunluk || 0).toLocaleString('tr-TR')} ₺</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-xs pt-1 border-t border-slate-100">
                                  <span className="text-slate-400">Toplam ({otomatikFiyat.kisiBasiHesap.gece} gece)</span>
                                  <span className="font-bold text-indigo-600">
                                    {((otomatikFiyat.kisiBasiHesap.toplam || 0) + cocukToplamFiyat(otomatikFiyat.kisiBasiHesap.gunluk || 0)).toLocaleString('tr-TR')} ₺
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                {otomatikFiyat.karma ? (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-400">Oda ({otomatikFiyat.gece} gece)</span>
                                    <span className="font-semibold text-amber-600">{(otomatikFiyat.odaToplam || 0).toLocaleString('tr-TR')} ₺ *</span>
                                  </div>
                                ) : (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-400">Oda gecelik</span>
                                    <span className="font-semibold text-slate-700">{(otomatikFiyat.gunluk || 0).toLocaleString('tr-TR')} ₺</span>
                                  </div>
                                )}
                                {otomatikFiyat.pansiyonEk > 0 && (
                                  <div className="flex justify-between text-xs"><span className="text-slate-400">Pansiyon ek</span>
                                    <span className="font-semibold text-emerald-600">+{otomatikFiyat.pansiyonEk.toLocaleString('tr-TR')} ₺</span></div>
                                )}
                                {cocukSayisi > 0 && (() => {
                                  const ucretsizS = cocukSayisi - ucretliCocukSayisi;
                                  const gece = otomatikFiyat.gece || 1;
                                  const baseG = otomatikFiyat.gunluk || 0;
                                  return (<>
                                    {ucretsizS > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-slate-400">👶 {ucretsizS} çocuk (kapasite içi)</span>
                                        <span className="font-semibold text-green-600">Ücretsiz</span>
                                      </div>
                                    )}
                                    {cocukYaslari.slice(ucretsizS).map((yas, i) => {
                                      const fiyat = cocukGecelikFiyat(yas, baseG);
                                      return (
                                        <div key={i} className="flex justify-between text-xs">
                                          <span className="text-slate-400">👶 {ucretsizS + i + 1}. çocuk ({yas} yaş)</span>
                                          <span className="font-semibold text-emerald-600">+{(fiyat * gece).toLocaleString('tr-TR')} ₺</span>
                                        </div>
                                      );
                                    })}
                                  </>);
                                })()}
                                {cocukSayisi > 0 && cocukToplamFiyat(otomatikFiyat.gunluk || 0) > 0 && (
                                  <div className="flex justify-between text-xs font-semibold text-emerald-700">
                                    <span>Çocuk toplam</span>
                                    <span>+{cocukToplamFiyat(otomatikFiyat.gunluk || 0).toLocaleString('tr-TR')} ₺</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-xs pt-1 border-t border-slate-100">
                                  <span className="text-slate-400">Toplam ({otomatikFiyat.gece} gece)</span>
                                  <span className="font-bold text-indigo-600">
                                    {((otomatikFiyat.toplam || 0) + cocukToplamFiyat(otomatikFiyat.gunluk || 0)).toLocaleString('tr-TR')} ₺
                                  </span>
                                </div>
                                {otomatikFiyat.karma && (
                                  <p className="text-[10px] text-amber-500">* Özel + standart fiyat karışımı</p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2 — Misafirler */}
            {aktifTab === 2 && (
              <div style={{ animation: 'slideIn 0.2s ease-out' }} className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Sol: Misafir giriş formu */}
                <div className="space-y-4">
                  <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">✏️</span>
                      Yeni / Manuel Giriş
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Ülke — en başta, çünkü aşağıdaki alanları etkiliyor */}
                      <div>
                        <label className={labelClass}>Ülke *</label>
                        <select value={yeniMisafir.ulke} onChange={e => setYeniMisafir({ ...yeniMisafir, ulke: e.target.value, tc: '', pasaportNo: '', pasaportGecerlilik: '', uyruk: '' })} className={inputClass}>
                          <option>TÜRKİYE</option><option>ALMANYA</option><option>İNGİLTERE</option>
                          <option>RUSYA</option><option>FRANSA</option><option>ABD</option>
                          <option>HOLLANDA</option><option>BELÇİKA</option><option>İSVİÇRE</option>
                          <option>İTALYA</option><option>İSPANYA</option><option>JAPONYA</option>
                          <option>ÇİN</option><option>UKRAYNA</option><option>DİĞER</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Cinsiyet</label>
                        <select value={yeniMisafir.cinsiyet} onChange={e => setYeniMisafir({ ...yeniMisafir, cinsiyet: e.target.value })} className={inputClass}>
                          <option>Erkek</option><option>Kadın</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Yetişkin / Çocuk</label>
                        <div className="flex rounded-2xl overflow-hidden border border-slate-200 text-[10px]">
                          <button type="button"
                            onClick={() => setYeniMisafir({ ...yeniMisafir, grupTip: 'Yetişkin' })}
                            className={`flex-1 py-2.5 font-black uppercase tracking-widest transition-all ${yeniMisafir.grupTip === 'Yetişkin' ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                            👤 Yetişkin
                          </button>
                          <button type="button"
                            onClick={() => setYeniMisafir({ ...yeniMisafir, grupTip: 'Çocuk' })}
                            className={`flex-1 py-2.5 font-black uppercase tracking-widest transition-all border-l border-slate-200 ${yeniMisafir.grupTip === 'Çocuk' ? 'bg-gradient-to-r from-orange-400 to-amber-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                            🧒 Çocuk
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Misafir Tipi <span className="text-slate-300 font-normal normal-case">(opsiyonel)</span></label>
                        <select value={yeniMisafir.misafirTipiId} onChange={e => setYeniMisafir({ ...yeniMisafir, misafirTipiId: e.target.value })} className={inputClass}>
                          <option value="">— Seçiniz —</option>
                          {misafirTipleri.map(t => (
                            <option key={t.misafirTipiId} value={t.misafirTipiId}>
                              {t.ad}
                              {t.gecelikFiyat > 0 ? ` · ${t.gecelikFiyat.toLocaleString('tr-TR')} ₺/oda` : ''}
                              {t.kisiBazliFiyat > 0 ? ` · ${t.kisiBazliFiyat.toLocaleString('tr-TR')} ₺/kişi` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* TC veya Pasaport — ülkeye göre */}
                      {yeniMisafir.ulke === 'TÜRKİYE' ? (
                        <div className="md:col-span-2">
                          <label className={labelClass}>T.C. Kimlik No <span className="text-red-400">*</span></label>
                          <input value={yeniMisafir.tc} onChange={e => setYeniMisafir({ ...yeniMisafir, tc: e.target.value })}
                            className={inputClass + (yeniMisafir.tc && tcDogrula(yeniMisafir.tc) ? ' border-red-400 focus:ring-red-400' : '')}
                            placeholder="11 haneli TC kimlik no" maxLength={11} />
                          {yeniMisafir.tc && tcDogrula(yeniMisafir.tc) && (
                            <p className="text-red-500 text-xs mt-1">{tcDogrula(yeniMisafir.tc)}</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className={labelClass}>Pasaport No <span className="text-red-400">*</span></label>
                            <input value={yeniMisafir.pasaportNo} onChange={e => setYeniMisafir({ ...yeniMisafir, pasaportNo: e.target.value })}
                              className={inputClass + (yeniMisafir.pasaportNo && pasaportDogrula(yeniMisafir.pasaportNo) ? ' border-red-400 focus:ring-red-400' : '')}
                              placeholder="Pasaport numarası" />
                            {yeniMisafir.pasaportNo && pasaportDogrula(yeniMisafir.pasaportNo) && (
                              <p className="text-red-500 text-xs mt-1">{pasaportDogrula(yeniMisafir.pasaportNo)}</p>
                            )}
                          </div>
                          <div>
                            <label className={labelClass}>Pasaport Geçerlilik Tarihi</label>
                            <DatePicker value={yeniMisafir.pasaportGecerlilik} onChange={val => setYeniMisafir({ ...yeniMisafir, pasaportGecerlilik: val })} placeholder="GG.AA.YYYY" />
                          </div>
                          <div>
                            <label className={labelClass}>Uyruk / Milliyet</label>
                            <input value={yeniMisafir.uyruk} onChange={e => setYeniMisafir({ ...yeniMisafir, uyruk: e.target.value })}
                              className={inputClass} placeholder="Örn: Alman, Rus..." />
                          </div>
                        </>
                      )}

                      <div>
                        <label className={labelClass}>Ad Soyad *</label>
                        <input value={yeniMisafir.adSoyad} onChange={e => setYeniMisafir({ ...yeniMisafir, adSoyad: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Doğum Tarihi</label>
                        <DatePicker value={yeniMisafir.dogum} onChange={val => setYeniMisafir({ ...yeniMisafir, dogum: val })} placeholder="GG.AA.YYYY" />
                      </div>
                      <div>
                        <label className={labelClass}>Doğum Yeri</label>
                        <input value={yeniMisafir.dogumYeri} onChange={e => setYeniMisafir({ ...yeniMisafir, dogumYeri: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Ana Adı</label>
                        <input value={yeniMisafir.anaAdi} onChange={e => setYeniMisafir({ ...yeniMisafir, anaAdi: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Baba Adı</label>
                        <input value={yeniMisafir.babaAdi} onChange={e => setYeniMisafir({ ...yeniMisafir, babaAdi: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Araç Plaka</label>
                        <input value={yeniMisafir.aracPlaka} onChange={e => setYeniMisafir({ ...yeniMisafir, aracPlaka: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Telefon</label>
                        <input value={yeniMisafir.telefon} onChange={e => setYeniMisafir({ ...yeniMisafir, telefon: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>E-mail</label>
                        <input value={yeniMisafir.email} onChange={e => setYeniMisafir({ ...yeniMisafir, email: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Meslek</label>
                        <input value={yeniMisafir.meslek} onChange={e => setYeniMisafir({ ...yeniMisafir, meslek: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Firma</label>
                        <input value={yeniMisafir.firma} onChange={e => setYeniMisafir({ ...yeniMisafir, firma: e.target.value })} className={inputClass} placeholder="Şirket / Kurum (zorunlu değil)" />
                      </div>
                      <div>
                        <label className={labelClass}>Adres</label>
                        <input value={yeniMisafir.adres} onChange={e => setYeniMisafir({ ...yeniMisafir, adres: e.target.value })} className={inputClass} />
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button onClick={misafirEkle}
                        className="flex-1 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:shadow-xl transition-all">
                        + Listeye Ekle
                      </button>
                      <button onClick={() => setYeniMisafir(BOŞ_MİSAFİR)}
                        className="px-4 bg-slate-100 hover:bg-slate-100 text-slate-600 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all">
                        Temizle
                      </button>
                    </div>
                  </div>

                  {/* Rezervasyona eklenecek misafirler */}
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-100 px-4 py-2.5 flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rezervasyona Eklenecekler</span>
                      <div className="flex items-center gap-1.5 text-xs font-bold">
                        {(() => {
                          const ekYetiskin = misafirler.filter(m => m.grupTip === 'Yetişkin').length;
                          const ekCocuk = misafirler.filter(m => m.grupTip === 'Çocuk').length;
                          const yOk = ekYetiskin >= kisiSayisi;
                          const cOk = cocukSayisi === 0 || ekCocuk >= cocukSayisi;
                          return (
                            <>
                              <span className={`px-2 py-0.5 rounded-lg ${yOk ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                👤 {ekYetiskin}/{kisiSayisi}
                              </span>
                              {cocukSayisi > 0 && (
                                <span className={`px-2 py-0.5 rounded-lg ${cOk ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  🧒 {ekCocuk}/{cocukSayisi}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {misafirler.length === 0 ? (
                      <div className="text-center py-6 text-slate-400 text-xs">Henüz misafir eklenmedi</div>
                    ) : (
                      <>
                        {/* Masaüstü: tablo */}
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead><tr className="bg-slate-50 border-b border-slate-100">
                              {['TC', 'Ad Soyad', 'Tip', 'Misafir Tipi', 'Ülke', 'Cinsiyet', ''].map(h => (
                                <th key={h} className="text-left py-2 px-3 font-semibold text-slate-500">{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {misafirler.map((m, i) => (
                                <tr key={i} className="border-b border-gray-50 hover:bg-rose-50/50">
                                  <td className="py-2 px-3 font-mono">{m.tc || m.pasaportNo || '—'}</td>
                                  <td className="py-2 px-3 font-semibold text-slate-800">{m.adSoyad}</td>
                                  <td className="py-2 px-3">
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${m.grupTip === 'Çocuk' ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                      {m.grupTip === 'Çocuk' ? '🧒' : '👤'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3">
                                    <select
                                      value={m.misafirTipiId || ''}
                                      onChange={e => setMisafirler(prev => prev.map((x, idx) => idx === i ? { ...x, misafirTipiId: e.target.value } : x))}
                                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-[100px]">
                                      <option value="">—</option>
                                      {misafirTipleri.map(t => (
                                        <option key={t.misafirTipiId} value={t.misafirTipiId}>{t.ad}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="py-2 px-3">{m.ulke}</td>
                                  <td className="py-2 px-3">{m.cinsiyet}</td>
                                  <td className="py-2 px-3">
                                    <button type="button" onClick={e => { e.stopPropagation(); setMisafirler(prev => prev.filter((_, idx) => idx !== i)); }}
                                      className="text-red-400 hover:text-red-600 font-bold text-base px-1">✕</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Mobil: kart */}
                        <div className="sm:hidden divide-y divide-gray-50">
                          {misafirler.map((m, i) => (
                            <div key={i} className="flex items-center justify-between py-2.5 px-3 hover:bg-rose-50/50">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${m.grupTip === 'Çocuk' ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                  {m.grupTip === 'Çocuk' ? '🧒' : '👤'}
                                </span>
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-800 text-xs truncate">{m.adSoyad}</p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[10px] text-slate-400">{m.tc || m.pasaportNo || '—'}</span>
                                    <span className="text-[10px] text-slate-400">{m.ulke}</span>
                                    <span className="text-[10px] text-slate-400">{m.cinsiyet}</span>
                                    {yasHesapla(m.dogum) != null && <span className="text-[10px] text-slate-400">{yasHesapla(m.dogum)} yaş</span>}
                                  </div>
                                </div>
                              </div>
                              <button type="button" onClick={e => { e.stopPropagation(); setMisafirler(prev => prev.filter((_, idx) => idx !== i)); }}
                                className="flex-shrink-0 ml-2 w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full font-bold text-sm transition-colors">✕</button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Sağ: Kayıtlı misafirler */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden flex flex-col">
                  <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
                    <span className="text-white text-[10px] font-black uppercase tracking-widest">📋 Kayıtlı Misafirler</span>
                    <span className="text-white/60 text-xs font-semibold">{kayitliMisafirler.length} kayıt</span>
                  </div>
                  <div className="p-3 border-b border-slate-100 flex-shrink-0">
                    <input
                      value={misafirArama}
                      onChange={e => setMisafirArama(e.target.value)}
                      placeholder="Ad, TC veya telefon ile ara..."
                      className="w-full border border-slate-200 rounded-2xl px-3 py-2 text-xs bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-all"
                    />
                  </div>
                  <div className="overflow-auto flex-1" style={{ maxHeight: '400px', WebkitOverflowScrolling: 'touch' }}>
                    {kayitliMisafirler.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-xs">
                        <div className="text-3xl mb-2">👥</div>Kayıtlı misafir bulunamadı
                      </div>
                    ) : (
                      <table className="w-full text-xs min-w-[480px]">
                        <thead className="sticky top-0 bg-slate-50 z-10">
                          <tr className="border-b border-slate-200">
                            {['Ad Soyad', 'TC', 'Ülke', 'Tel', 'Cinsiyet', 'Yaş (D.T.)', 'Firma'].map(h => (
                              <th key={h} className="text-left py-2 px-3 font-semibold text-slate-500">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {kayitliMisafirler
                            .filter(m => {
                              if (!misafirArama) return true;
                              const ara = misafirArama.toLowerCase();
                              return (m.adSoyad || '').toLowerCase().includes(ara) ||
                                (m.tcPasaportNo || '').toLowerCase().includes(ara) ||
                                (m.pasaportNo || '').toLowerCase().includes(ara) ||
                                (m.telefon || '').toLowerCase().includes(ara);
                            })
                            .map((m, i) => {
                              const zatenEklendi = misafirler.some(x =>
                                (x.tc && x.tc === m.tcPasaportNo) ||
                                (x.pasaportNo && x.pasaportNo === m.pasaportNo)
                              );
                              return (
                                <tr key={i}
                                  onClick={() => {
                                    if (zatenEklendi) return;
                                    // Forma doldur
                                    const formatDB = (iso) => {
                                      if (!iso) return '';
                                      const d = new Date(iso);
                                      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
                                    };
                                    const suggestGrupTip = (() => {
                                      const ekYetiskin = misafirler.filter(x => x.grupTip === 'Yetişkin').length;
                                      return ekYetiskin < kisiSayisi ? 'Yetişkin' : 'Çocuk';
                                    })();
                                    setYeniMisafir({
                                      tc: m.ulke === 'TÜRKİYE' ? (m.tcPasaportNo || '') : '',
                                      pasaportNo: m.ulke !== 'TÜRKİYE' ? (m.pasaportNo || m.tcPasaportNo || '') : '',
                                      pasaportGecerlilik: formatDB(m.pasaportGecerlilikTarihi),
                                      uyruk: m.uyruk || '',
                                      adSoyad: m.adSoyad || '',
                                      dogum: formatDB(m.dogumTarihi),
                                      dogumYeri: m.dogumYeri || '',
                                      ulke: m.ulke || 'TÜRKİYE',
                                      telefon: m.telefon || '',
                                      email: m.email || '',
                                      cinsiyet: m.cinsiyet || 'Erkek',
                                      meslek: m.meslek || '',
                                      adres: m.adres || '',
                                      firma: m.firma || '',
                                      grupTip: suggestGrupTip,
                                      misafirTipiId: '',
                                    });
                                  }}
                                  className={`border-b border-gray-50 transition-colors
                                  ${zatenEklendi ? 'bg-green-50 opacity-60 cursor-not-allowed' : 'hover:bg-indigo-50 cursor-pointer'}`}>
                                  <td className="py-2.5 px-3 font-semibold text-slate-800">
                                    {m.adSoyad}
                                    {zatenEklendi && <span className="ml-1 text-green-600 font-bold">✓</span>}
                                  </td>
                                  <td className="py-2.5 px-3 font-mono text-slate-500">{m.tcPasaportNo || m.pasaportNo || '—'}</td>
                                  <td className="py-2.5 px-3 text-slate-500">{m.ulke || '—'}</td>
                                  <td className="py-2.5 px-3 text-slate-500">{m.telefon || '—'}</td>
                                  <td className="py-2.5 px-3 text-slate-500">{m.cinsiyet || '—'}</td>
                                  <td className="py-2.5 px-3 text-slate-500">{yasHesapla(m.dogumTarihi) != null ? yasHesapla(m.dogumTarihi) : '—'}</td>
                                  <td className="py-2.5 px-3 text-slate-500">{m.firma || '—'}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="px-3 py-2 bg-indigo-50 border-t border-indigo-100 text-[10px] text-indigo-600 font-black uppercase tracking-widest flex-shrink-0">
                    💡 Kayıtlı misafire tıkla → form dolar → Listeye Ekle
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3 — Fiyat */}
            {aktifTab === 3 && (
              <div style={{ animation: 'slideIn 0.2s ease-out' }}>
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-5 flex items-center gap-2">
                    <span className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">💰</span>
                    Fiyat Bilgileri
                  </h3>

                  {/* Otomatik fiyat banner */}
                  {otomatikFiyat && fiyatTipi !== 'Fiyatı sabitle' && (() => {
                    const baseGecelik = fiyatTipi === 'Kişi bazlı gecelik' || fiyatTipi === 'Kişi bazlı fiyat'
                      ? (otomatikFiyat.kisiBasiHesap?.gunluk || 0)
                      : (otomatikFiyat.gunluk || 0);
                    const baseToplam = fiyatTipi === 'Kişi bazlı fiyat' && otomatikFiyat.kisiBasiHesap
                      ? (otomatikFiyat.kisiBasiHesap.toplam || 0)
                      : (otomatikFiyat.toplam || 0);
                    const cocukToplam = cocukSayisi > 0 ? cocukToplamFiyat(baseGecelik) : 0;
                    const genelToplam = baseToplam + cocukToplam;
                    return (
                      <div className={`border rounded-2xl px-4 py-3 mb-5 ${otomatikFiyat.belirsiz ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200'}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                          <span className={`text-sm font-bold ${otomatikFiyat.belirsiz ? 'text-amber-700' : 'text-indigo-700'}`}>
                            {otomatikFiyat.belirsiz
                              ? (fiyatTipi === 'Kişi bazlı gecelik' ? 'Kişi bazlı gecelik ücret' : 'Gecelik ücret belirlendi')
                              : fiyatTipi === 'Kişi bazlı fiyat' ? 'Kişi bazlı hesaplandı'
                                : 'Otomatik hesaplandı'}
                          </span>
                          <span className={`text-base font-bold whitespace-nowrap ${otomatikFiyat.belirsiz ? 'text-amber-700' : 'text-indigo-700'}`}>
                            {otomatikFiyat.belirsiz
                              ? (<>
                                {(odaTutari || 0).toLocaleString('tr-TR')} {paraBirimi} / gece
                                {paraBirimi !== 'TL' && paraBirimi !== 'TRY' && dovizKurlari[paraBirimi] > 0 && (
                                  <span className="text-sm font-normal ml-1 opacity-70">≈ {Math.round((odaTutari || 0) * dovizKurlari[paraBirimi]).toLocaleString('tr-TR')} ₺</span>
                                )}
                              </>)
                              : (<>
                                {(paraBirimi !== 'TL' && paraBirimi !== 'TRY' && dovizKurlari[paraBirimi] > 0
                                  ? (Math.round(genelToplam / dovizKurlari[paraBirimi] * 100) / 100)
                                  : genelToplam).toLocaleString('tr-TR')} {paraBirimi}
                                {paraBirimi !== 'TL' && paraBirimi !== 'TRY' && (
                                  <span className="text-sm font-normal ml-1 opacity-70">≈ {genelToplam.toLocaleString('tr-TR')} ₺</span>
                                )}
                              </>)}
                          </span>
                        </div>
                        <div className={`text-xs space-y-0.5 ${otomatikFiyat.belirsiz ? 'text-amber-600' : 'text-indigo-600'}`}>
                          {fiyatTipi === 'Kişi bazlı gecelik' && otomatikFiyat.kisiBasiHesap && otomatikFiyat.belirsiz && (
                            <div>👤 {kisiSayisi} kişi gecelik: {(otomatikFiyat.kisiBasiHesap.gecelikOda || 0).toLocaleString('tr-TR')} ₺ + pansiyon</div>
                          )}
                          {fiyatTipi === 'Kişi bazlı fiyat' && otomatikFiyat.kisiBasiHesap && !otomatikFiyat.belirsiz && (
                            <div>👤 {otomatikFiyat.kisiBasiHesap.kisiSayisi} kişi × {(otomatikFiyat.kisiBasiHesap.gunluk || 0).toLocaleString('tr-TR')} ₺ × {otomatikFiyat.kisiBasiHesap.gece} gece = {baseToplam.toLocaleString('tr-TR')} ₺</div>
                          )}
                          {fiyatTipi !== 'Kişi bazlı fiyat' && fiyatTipi !== 'Kişi bazlı gecelik' && !otomatikFiyat.belirsiz && (
                            otomatikFiyat.karma
                              ? <div>Oda: {(otomatikFiyat.odaToplam || 0).toLocaleString('tr-TR')} ₺ ({otomatikFiyat.gece} gece, karışık fiyat)</div>
                              : <div>Oda: {(otomatikFiyat.gunluk || 0).toLocaleString('tr-TR')} ₺ × {otomatikFiyat.gece} gece = {baseToplam.toLocaleString('tr-TR')} ₺</div>
                          )}
                          {otomatikFiyat.pansiyonEk > 0 && (
                            <div>Pansiyon ek: +{otomatikFiyat.pansiyonEk.toLocaleString('tr-TR')} ₺ / gece</div>
                          )}
                          {cocukSayisi > 0 && (() => {
                            const ucretsizSayisi = cocukSayisi - ucretliCocukSayisi;
                            const ucretliYaslar = cocukYaslari.slice(cocukYaslari.length - ucretliCocukSayisi);
                            return (
                              <>
                                {ucretsizSayisi > 0 && (
                                  <div>👶 {ucretsizSayisi} çocuk kapasite içinde — <span className="font-semibold text-green-600">Ücretsiz</span></div>
                                )}
                                {ucretliYaslar.map((yas, i) => {
                                  const fiyat = cocukGecelikFiyat(yas, baseGecelik);
                                  const kural = cocukKurallari.find(k => yas >= k.minYas && yas <= k.maxYas);
                                  return (
                                    <div key={i}>
                                      👶 {ucretsizSayisi + i + 1}. çocuk ({yas} yaş / {kural?.ad || '?'}): +{(fiyat * (otomatikFiyat.gece || 1)).toLocaleString('tr-TR')} ₺
                                    </div>
                                  );
                                })}
                                {cocukToplam > 0 && !otomatikFiyat.belirsiz && (
                                  <div className="font-semibold pt-0.5 border-t border-current border-opacity-20">
                                    Çocuk toplam: +{cocukToplam.toLocaleString('tr-TR')} ₺ → Genel toplam: {genelToplam.toLocaleString('tr-TR')} ₺
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          {otomatikFiyat.belirsiz && <div>Toplam, check-out anında gece sayısına göre hesaplanacak.</div>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Firma Fiyatlandırma Seçici ──────────────────────────── */}
                  {secilenFirmaId && firmaKurallari.length > 0 ? (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 px-2.5 py-1 rounded-full">🏢 Firma Fiyatlandırması</span>
                        {cikisBelir && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-100">Gecelik ücret modu</span>}
                      </div>
                      <div className="space-y-2">
                        {firmaKurallari.map(c => (
                          <label key={c.kural.firmaFiyatKuraliId}
                            className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 cursor-pointer transition-all
                            ${secilenFirmaKuraliId === c.kural.firmaFiyatKuraliId
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30'}`}>
                            <input type="radio" name="firmaKural"
                              checked={secilenFirmaKuraliId === c.kural.firmaFiyatKuraliId}
                              onChange={() => setSecilenFirmaKuraliId(c.kural.firmaFiyatKuraliId)}
                              className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                                {c.kural.odaTipiAd && <span className="text-xs text-slate-400">({c.kural.odaTipiAd})</span>}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">{c.detayStr}</div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="text-base font-bold text-indigo-700 whitespace-nowrap">
                                {cikisBelir
                                  ? `${c.gecelikToplam.toLocaleString('tr-TR')} ${c.paraBirimi}/gece`
                                  : `${c.toplam.toLocaleString('tr-TR')} ${c.paraBirimi}`}
                              </span>
                              {(cikisBelir ? c.tryGecelik : c.tryToplam) != null && (
                                <div className="text-xs text-slate-400 mt-0.5">
                                  ≈ {(cikisBelir ? c.tryGecelik : c.tryToplam).toLocaleString('tr-TR')} ₺
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                      {/* Fiyatı sabitle çıkış yolu */}
                      <div className="mt-2">
                        <label className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 cursor-pointer transition-all
                        ${fiyatTipi === 'Fiyatı sabitle' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-100 hover:border-slate-300'}`}>
                          <input type="radio" name="firmaKural" checked={fiyatTipi === 'Fiyatı sabitle'}
                            onChange={() => { setFiyatTipi('Fiyatı sabitle'); setSecilenFirmaKuraliId(null); }}
                            className="w-4 h-4 text-indigo-500" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Manuel fiyat gir</span>
                        </label>
                      </div>
                      {cikisBelir && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2.5 text-xs text-amber-700 font-semibold">
                          Çıkış tarihi belirsiz — toplam check-out sırasında otomatik hesaplanacak.
                        </div>
                      )}
                    </div>
                  ) : cikisBelir ? (
                    /* Normal belirsiz mod */
                    <div className="mb-6 space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-sm text-amber-700 font-semibold">
                        Çıkış tarihi belirsiz — <strong>Gecelik Ücret</strong> modu aktif. Toplam check-out sırasında otomatik hesaplanacak.
                      </div>
                      {(otomatikFiyat?.kisiBasiMevcut || misafirTipiGecelikAktif || misafirTipiKisiBasiAktif) && (
                        <div className="flex gap-3 flex-wrap">
                          {[
                            { key: 'Gecelik ücret', label: 'Oda gecelik fiyatı', sub: `${(otomatikFiyat?.gecelik || 0).toLocaleString('tr-TR')} ₺ / gece`, show: true },
                            { key: 'Kişi bazlı gecelik', label: `Kişi bazlı (${kisiSayisi} kişi)`, sub: `${(otomatikFiyat?.kisiBasiHesap?.gecelik || 0).toLocaleString('tr-TR')} ₺ / gece`, show: !!otomatikFiyat?.kisiBasiMevcut },
                            { key: 'Misafir Tipi Gecelik', label: 'M.Tipi Gecelik', sub: `${misafirTipiGecelikFiyat.toLocaleString('tr-TR')} ₺ / gece`, show: misafirTipiGecelikAktif },
                            { key: 'Misafir Tipi Kişi Bazlı', label: 'M.Tipi Kişi Bazlı', sub: `${misafirTipiKisiBasiGecelik.toLocaleString('tr-TR')} ₺ / gece`, show: misafirTipiKisiBasiAktif },
                          ].filter(o => o.show).map(opt => (
                            <label key={opt.key} className={`flex-1 flex items-start gap-2 px-4 py-3 rounded-2xl border-2 cursor-pointer transition-all min-w-[140px]
                            ${fiyatTipi === opt.key ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'}`}>
                              <input type="radio" name="belirsizMod" checked={fiyatTipi === opt.key} onChange={() => {
                                setFiyatTipi(opt.key);
                                if (opt.key === 'Kişi bazlı gecelik') setOdaTutari(otomatikFiyat?.kisiBasiHesap?.gecelik || 0);
                                else if (opt.key === 'Misafir Tipi Gecelik') setOdaTutari(misafirTipiGecelikFiyat);
                                else if (opt.key === 'Misafir Tipi Kişi Bazlı') setOdaTutari(misafirTipiKisiBasiGecelik);
                                else setOdaTutari(otomatikFiyat?.gecelik || 0);
                              }} className="mt-0.5 w-4 h-4 text-indigo-500" />
                              <div>
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">{opt.label}</div>
                                <div className="text-xs text-slate-400 mt-0.5">{opt.sub}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Normal mod */
                    <div className="flex gap-3 mb-6 flex-wrap">
                      {[
                        { key: 'Toplam fiyat', label: 'Toplam fiyat' },
                        ...(otomatikFiyat?.kisiBasiMevcut ? [{ key: 'Kişi bazlı fiyat', label: `Kişi bazlı (${otomatikFiyat.kisiBasiHesap?.kisiSayisi} kişi)` }] : []),
                        ...(misafirTipiGecelikAktif ? [{ key: 'Misafir Tipi Gecelik', label: `M.Tipi Gecelik · ${misafirTipiGecelikFiyat.toLocaleString('tr-TR')} ₺` }] : []),
                        ...(misafirTipiKisiBasiAktif ? [{ key: 'Misafir Tipi Kişi Bazlı', label: `M.Tipi Kişi Bazlı · ${misafirTipiKisiBasiGecelik.toLocaleString('tr-TR')} ₺/gece` }] : []),
                        { key: 'Günlük-Özel fiyat', label: 'Günlük-Özel fiyat' },
                        { key: 'Fiyatı sabitle', label: 'Fiyatı sabitle' },
                      ].map(({ key: f, label }) => (
                        <label key={f} className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 cursor-pointer transition-all
                        ${fiyatTipi === f ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'}`}>
                          <input type="radio" name="fiyat" checked={fiyatTipi === f} onChange={() => {
                            setFiyatTipi(f);
                            if (f === 'Toplam fiyat' && otomatikFiyat) setOdaTutari(otomatikFiyat.toplam || 0);
                            if (f === 'Kişi bazlı fiyat' && otomatikFiyat?.kisiBasiHesap) setOdaTutari(otomatikFiyat.kisiBasiHesap.toplam || 0);
                            if (f === 'Misafir Tipi Gecelik') setOdaTutari(misafirTipiGecelikFiyat * geceSayisi);
                            if (f === 'Misafir Tipi Kişi Bazlı') setOdaTutari(misafirTipiKisiBasiGecelik * geceSayisi);
                            if (f === 'Günlük-Özel fiyat' && geceSayisi > 0) {
                              const gunlukOrt = otomatikFiyat ? Math.round((otomatikFiyat.odaToplam || 0) / geceSayisi) : 0;
                              setGunlukFiyatlar(Array.from({ length: geceSayisi }, (_, i) => {
                                const gun = new Date(parseTarih(girisTarihi));
                                gun.setDate(gun.getDate() + i);
                                return { gun: gun.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', weekday: 'short' }), fiyat: gunlukOrt };
                              }));
                            }
                          }} className="w-4 h-4 text-indigo-500" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">{label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* GECELİK ÜCRET (çıkış belirsiz) */}
                  {fiyatTipi === 'Gecelik ücret' && (
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex-1">
                        <label className={labelClass}>
                          Gecelik Ücret
                          {fiyatYukleniyor && <span className="ml-2 text-amber-400 text-xs font-normal animate-pulse">hesaplanıyor...</span>}
                        </label>
                        <input type="number" value={odaTutari} onChange={e => setOdaTutari(e.target.value)} className={inputClass} />
                      </div>
                      <div className="w-28">
                        <label className={labelClass}>Para Birimi</label>
                        <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} className={inputClass}>
                          {paraBirimleri.length > 0
                            ? paraBirimleri.map(pb => <option key={pb.paraBirimiId} value={pb.tcmbKod}>{pb.tcmbKod}</option>)
                            : [<option key="TL">TL</option>, <option key="USD">USD</option>, <option key="EUR">EUR</option>]
                          }
                        </select>
                      </div>
                    </div>
                  )}

                  {/* TOPLAM FİYAT */}
                  {fiyatTipi === 'Toplam fiyat' && (
                    <div className="mb-6">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className={labelClass}>
                            Toplam Tutar
                            {fiyatYukleniyor && <span className="ml-2 text-indigo-400 text-xs font-normal animate-pulse">hesaplanıyor...</span>}
                          </label>
                          <input type="number"
                            value={(parseFloat(odaTutari) || 0) + (cocukSayisi > 0 ? cocukToplamFiyat(otomatikFiyat?.gunluk || 0) : 0)}
                            onChange={e => setOdaTutari(e.target.value)} className={inputClass} />
                        </div>
                        <div className="w-28">
                          <label className={labelClass}>Para Birimi</label>
                          <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} className={inputClass}>
                            {paraBirimleri.length > 0
                              ? paraBirimleri.map(pb => <option key={pb.paraBirimiId} value={pb.tcmbKod}>{pb.tcmbKod}</option>)
                              : [<option key="TL">TL</option>, <option key="USD">USD</option>, <option key="EUR">EUR</option>]
                            }
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* GÜNLÜK-ÖZEL FİYAT */}
                  {fiyatTipi === 'Günlük-Özel fiyat' && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <label className={labelClass}>Her Gece İçin Fiyat Girin</label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-indigo-600">
                            Toplam: {gunlukFiyatlar.reduce((s, g) => s + (parseFloat(g.fiyat) || 0), 0).toLocaleString('tr-TR')} ₺
                          </span>
                          <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)}
                            className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none bg-slate-50">
                            {paraBirimleri.length > 0
                              ? paraBirimleri.map(pb => <option key={pb.paraBirimiId} value={pb.tcmbKod}>{pb.tcmbKod}</option>)
                              : [<option key="TL">TL</option>, <option key="USD">USD</option>, <option key="EUR">EUR</option>]
                            }
                          </select>
                        </div>
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {gunlukFiyatlar.map((g, i) => (
                          <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-2xl px-4 py-2.5 border border-slate-100">
                            <span className="text-xs font-bold text-slate-500 w-6">{i + 1}.</span>
                            <span className="text-xs text-slate-600 w-28 flex-shrink-0">{g.gun}</span>
                            <input
                              type="number" min="0" value={g.fiyat}
                              onChange={e => {
                                const yeni = [...gunlukFiyatlar];
                                yeni[i] = { ...yeni[i], fiyat: e.target.value };
                                setGunlukFiyatlar(yeni);
                                setOdaTutari(yeni.reduce((s, x) => s + (parseFloat(x.fiyat) || 0), 0));
                              }}
                              className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                            />
                            <span className="text-xs text-slate-400 flex-shrink-0">₺</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">Her gece ayrı fiyat girebilirsiniz. Toplam otomatik hesaplanır.</p>
                    </div>
                  )}

                  {/* FİYATI SABİTLE */}
                  {fiyatTipi === 'Fiyatı sabitle' && (
                    <div className="mb-6">
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4 text-xs text-amber-700 font-semibold">
                        ⚠️ Sistem fiyatı görmezden gelinir. Girilen tutar doğrudan kaydedilir.
                        {otomatikFiyat && <span className="ml-1">(Sistem önerisi: <b>{otomatikFiyat.toplam.toLocaleString('tr-TR')} ₺</b>)</span>}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className={labelClass}>Sabit Tutar</label>
                          <input type="number" value={odaTutari} onChange={e => setOdaTutari(e.target.value)}
                            className={inputClass + ' text-lg font-bold'} placeholder="0" />
                        </div>
                        <div className="w-28">
                          <label className={labelClass}>Para Birimi</label>
                          <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} className={inputClass}>
                            {paraBirimleri.length > 0
                              ? paraBirimleri.map(pb => <option key={pb.paraBirimiId} value={pb.tcmbKod}>{pb.tcmbKod}</option>)
                              : [<option key="TL">TL</option>, <option key="USD">USD</option>, <option key="EUR">EUR</option>]
                            }
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Kapora */}
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <label className={`flex items-center gap-2 cursor-pointer px-4 py-3 rounded-2xl border-2 transition-all w-full sm:w-auto
                      ${kaporaAlindi ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-200 hover:border-emerald-200'}`}>
                        <input type="checkbox" checked={kaporaAlindi} onChange={e => {
                          setKaporaAlindi(e.target.checked);
                          if (!e.target.checked) setKaporaTutari('');
                        }} className="w-4 h-4" />
                        <span className={`text-[10px] font-black uppercase tracking-widest ${kaporaAlindi ? 'text-emerald-700' : 'text-slate-600'}`}>
                          Ödeme / Kapora Alındı
                        </span>
                      </label>
                      <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-2xl px-4 py-3 w-full sm:w-auto border border-slate-100">
                        <span>👤</span>
                        <span className="font-semibold">Personel: <strong className="text-slate-700">{personel?.adSoyad || 'Bilinmiyor'}</strong></span>
                      </div>
                    </div>

                    {kaporaAlindi && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-3">Kapora bilgilerini girin — rezervasyon kaydedildiğinde otomatik ödeme kaydı oluşturulur.</p>
                        {(() => {
                          const bankaGerekli = kaporaOdemeTipi && !kaporaOdemeTipi.toLowerCase().includes('nakit') && bankalar.length > 0;
                          const kur = dovizKurlari[kaporaParaBirimi] || 1;
                          const tlKarsıligi = (parseFloat(kaporaTutari) || 0) * kur;
                          const yabancıPara = kaporaParaBirimi !== 'TL' && kaporaParaBirimi !== 'TRY';
                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {/* Tutar */}
                              <div>
                                <label className={labelClass}>Kapora Tutarı</label>
                                <div className="flex gap-2">
                                  <input
                                    type="number" min="0" step="0.01"
                                    value={kaporaTutari}
                                    onChange={e => setKaporaTutari(e.target.value)}
                                    placeholder="0.00"
                                    className={inputClass + ' font-bold'}
                                  />
                                  <select value={kaporaParaBirimi} onChange={e => setKaporaParaBirimi(e.target.value)} className={inputClass + ' w-20'}>
                                    {paraBirimleri.length > 0
                                      ? paraBirimleri.map(pb => <option key={pb.paraBirimiId} value={pb.tcmbKod}>{pb.tcmbKod}</option>)
                                      : ['TL', 'USD', 'EUR'].map(k => <option key={k}>{k}</option>)
                                    }
                                  </select>
                                </div>
                                {yabancıPara && parseFloat(kaporaTutari) > 0 && (
                                  <p className="text-xs text-indigo-600 mt-1">≈ {tlKarsıligi.toFixed(2)} TL (1 {kaporaParaBirimi} = {kur.toFixed(4)} TL)</p>
                                )}
                              </div>
                              {/* Ödeme Tipi */}
                              <div>
                                <label className={labelClass}>Ödeme Tipi</label>
                                <select value={kaporaOdemeTipi} onChange={e => { setKaporaOdemeTipi(e.target.value); setKaporaBankaAdi(''); }} className={inputClass}>
                                  <option value="">Seçiniz</option>
                                  {odemeTipleri.length > 0
                                    ? odemeTipleri.map(o => <option key={o.odemeTipiId} value={o.ad}>{o.ad}</option>)
                                    : ['NAKİT', 'KREDİ KARTI', 'BANKA HAVALE', 'ÇEK'].map(t => <option key={t}>{t}</option>)
                                  }
                                </select>
                              </div>
                              {/* Banka */}
                              {bankaGerekli && (
                                <div className="col-span-2">
                                  <label className={labelClass}>Banka</label>
                                  <select value={kaporaBankaAdi} onChange={e => setKaporaBankaAdi(e.target.value)} className={inputClass}>
                                    <option value="">Banka seçiniz</option>
                                    {bankalar.map(b => <option key={b.bankaTanimiId || b.ad} value={b.ad}>{b.ad}</option>)}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="bg-white border-t border-slate-100 px-5 py-3.5 flex justify-between items-center gap-3 flex-shrink-0">
            <div className="flex gap-2">
              {[1, 2, 3].map(n => (
                <button key={n} onClick={() => setAktifTab(n)}
                  className={`h-2.5 rounded-full transition-all ${aktifTab === n ? 'bg-indigo-500 w-6' : 'bg-slate-200 w-2.5 hover:bg-slate-300'}`} />
              ))}
            </div>
            <div className="flex gap-2 flex-1 justify-end">
              <button onClick={onClose} className="px-4 py-2.5 rounded-2xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all">İptal</button>
              <button onClick={handleKaydet} disabled={kaydediliyor}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:shadow-xl">
                {kaydediliyor ? '⏳ Kaydediliyor...' : <><span className="hidden sm:inline">💾 Rezervasyonu </span>Kaydet</>}
              </button>
            </div>
          </div>
        </div>

        <style>{`
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes slideIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
      </div>
    </>
    , document.body
  );
}